import React from "react";
import { useTranslation } from "react-i18next";
import { invalidate } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import { IoCloseOutline } from "react-icons/io5";
import { ProductsPanel } from "../../ProductsPanel";
import {
  UserModel3DLibraryItem,
  UserModels3DPanel,
  getUserModel3DInsertDimensions,
} from "../../UserModels3DPanel";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { editorStore } from "../../../../store/editorStore";
import {
  AreaFillSymbolType,
  CircleStairsType,
  CircleSymbolType,
  DoorType,
  DoubleDoorType,
  PolygonSymbolType,
  RectStairsType,
  BookShelfType,
  SlidingDoorType,
  SmartTableType,
  SquareSymbolType,
  SvgType,
  TriangleSymbolType,
  TurnStairsType,
  WallFaceSide,
  WallType,
} from "../../../../types/floorplannerTypes";
import {
  clearWallFaceMaterials,
  DEFAULT_WALL_FILL_COLOR,
  resolveWallFaceSurfaceRef,
  upsertWallFaceMaterial,
  withSideOneMirror,
} from "../../../../utils/wallFaceSides";
import { wallUsesDifferentSideColors } from "../../../../utils/fillSelection";
import { renderStore } from "../../../../store/renderStore";
import {
  CatalogImageOwner,
  useFloorplannerCategoriesMeta,
  useFloorplannerProducts,
} from "../../../../features/floorplan3d/catalog/catalogQueries";
import {
  findFlooringCategoryIds,
  findWallpaperCategoryIds,
} from "../../../../features/floorplan3d/catalog/wallpaperCategory";
import {
  usedWallpaperToCatalogOwner,
} from "../../../../features/floorplan3d/catalog/collectUsedWallpapers";
import { collectUsedWallpapersFromPlan } from "../../../../features/floorplan3d/catalog/collectUsedWallpapersFromPlan";
import {
  getCatalogCategorySnapshots,
  getCatalogEntryDisplayName,
  getCatalogItemDisplayName,
} from "../../../../features/floorplan3d/catalog/catalogDisplay";
import {
  BuiltInFurnitureMaterial,
  getBuiltInFurnitureMaterial,
} from "../../../../features/floorplan3d/materials/builtInFurnitureMaterials";
import {
  builtInFurnitureMaterialToSurfaceRefWithOverlay,
  getBuiltInMaterialCategoryKey,
  getBuiltInMaterialSurfaceMapping,
  listVisibleBuiltInFurnitureMaterials,
  listVisibleBuiltInFurnitureMaterialsByCategory,
  useFloorplannerBuiltInMaterialSurfaces,
} from "../../../../features/floorplan3d/materials/builtInMaterialSurfaceMappings";
import { canEditSolidBuiltInMaterialColor } from "../../../../features/floorplan3d/materials/materialColorHsb";
import {
  getProductMaterialOptionKeyForSlot,
  getProductMaterialOptions,
  listProductMaterialOptionSlots,
  type ProductMaterialOptionsConfig,
} from "../../../../features/floorplan3d/materials/productMaterialOptions";
import {
  getResizedCatalogImageUrl,
  getWallFloorCatalogImageUrl,
  getWallFloorProductTextureUrl,
  listWallFloorProductTextureImageOptions,
} from "../../../../features/floorplan3d/materials/textureCache";
import { getWall3DHeight } from "../../../../features/floorplan3d/geometry";
import {
  DEFAULT_WALL_TEXTURE_TILE_METERS,
  linkedWallTileMeters,
  wallTextureRepeatScaleFromTileMeters,
  wallTextureTileMetersFromRepeatScale,
} from "../../../../features/floorplan3d/materials/wallTextureTiling";
import {
  monitorLongTaskAfterMaterialAssignment,
  trackFloorplan3DMetric,
} from "../../../../features/floorplan3d/metrics/floorplan3DMetrics";
import {
  resolvePublishedProduct3DDimensions,
  scaleProduct3DDimensionsProportionally,
} from "../../../../features/floorplan3d/products/productDimensionUtils";
import {
  CatalogSurfaceReference,
  FloorPlan3DMaterialSlot,
} from "../../../../features/floorplan3d/products/product3DTypes";
import {
  getSearchTextForPrimitiveSymbol,
  resolveProductProxyForSymbol,
} from "../../../../features/floorplan3d/products/productProxyResolver";
import {
  applyFloorplannerSymbol3DDefaults,
  getFloorplannerSymbol3DDefaultKey,
  useFloorplannerSymbol3DDefaults,
} from "../../../../features/floorplan3d/symbols/defaults/floorplannerSymbol3DDefaults";
import { getPrimitiveSymbolMaterialSlots } from "../../../../features/floorplan3d/symbols/registry/primitiveSymbol3DConfig";
import {
  displayLengthToWorld,
  lengthUnitLabel,
  worldToDisplayLength,
} from "../../../../utils/conversions";
import { isSymbolAspectRatioLocked } from "../../../../utils/symbolAspectRatio";
import BuiltInMaterialsPanel from "../components/BuiltInMaterialsPanel";
import HsbColorSliders from "../components/HsbColorSliders";
import LinkedDimensionsGroup from "../components/LinkedDimensionsGroup";
import NumberField from "../components/NumberField";
import PanelSection from "../components/PanelSection";
import {
  omitSymbolDirectSurfaceMaterial,
  omitWallSurfaceMaterial,
} from "../propertyPanelUtils";

/** How many Ready swatches to show inline before "Browse materials". */
const MATERIAL_PANEL_PREVIEW_LIMIT = 9;
/** Wallpaper / flooring shortcuts under the catalog browse button. */
const CATALOG_SURFACE_PRODUCT_PREVIEW_LIMIT = 3;
/** Category-chip keys for wallpaper/flooring (shown next to Stone when walls/floors selected). */
const CATALOG_MATERIAL_CATEGORY_WALLPAPER = "catalog:wallpaper";
const CATALOG_MATERIAL_CATEGORY_FLOORING = "catalog:flooring";

const isCatalogMaterialCategory = (key: string | null): boolean =>
  key === CATALOG_MATERIAL_CATEGORY_WALLPAPER ||
  key === CATALOG_MATERIAL_CATEGORY_FLOORING;

type ShapeMaterialTarget =
  | SquareSymbolType
  | CircleSymbolType
  | TriangleSymbolType
  | PolygonSymbolType;

type StairMaterialTarget =
  | CircleStairsType
  | RectStairsType
  | TurnStairsType;

type DoorMaterialTarget = DoorType | SlidingDoorType | DoubleDoorType;

type MaterialEligibleSymbol = {
  threeD?: {
    sourceMode?: string;
    product?: { productId?: string } | null;
    userModel?: { modelId?: string; url?: string } | null;
  } | null;
};

/**
 * Catalog / brand / community / own 3D objects keep their baked materials.
 * Built-in Materials is for shapes, walls, area fills, and library Symbols.
 */
function usesExternalOrUploaded3D(symbol: MaterialEligibleSymbol): boolean {
  const threeD = symbol.threeD;
  if (!threeD) return false;
  if (threeD.sourceMode === "custom") return true;
  if (threeD.product?.productId) return true;
  if (threeD.userModel) return true;
  return false;
}

type ProductAndMaterialsSectionProps = {
  selectedWalls: WallType[];
  selectedAreaFillSymbols: AreaFillSymbolType[];
  selectedSvgs: SvgType[];
  selectedSmartTables?: SmartTableType[];
  selectedBookShelves?: BookShelfType[];
  selectedSquares?: SquareSymbolType[];
  selectedCircles?: CircleSymbolType[];
  selectedTriangles?: TriangleSymbolType[];
  selectedPolygons?: PolygonSymbolType[];
  selectedCircleStairs?: CircleStairsType[];
  selectedRectStairs?: RectStairsType[];
  selectedTurnStairs?: TurnStairsType[];
  selectedDoors?: Array<DoorType | SlidingDoorType>;
  selectedDoubleDoors?: DoubleDoorType[];
};

const THREE_D_SOURCE_PANEL_ENV_KEYS = [
  "REACT_APP_ENABLE_FLOORPLANNER_3D_SOURCE_PANEL",
  "REACT_APP_FLOORPLANNER_3D_SOURCE_PANEL",
];
const truthyEnvValues = new Set(["1", "true", "yes", "on"]);

function is3DSourcePanelEnabled(): boolean {
  return THREE_D_SOURCE_PANEL_ENV_KEYS.some((key) => {
    const value = process.env[key];

    return (
      typeof value === "string" && truthyEnvValues.has(value.toLowerCase())
    );
  });
}

const ProductAndMaterialsSection: React.FC<ProductAndMaterialsSectionProps> =
  observer((props) => {
    const { t } = useTranslation();
    const {
      selectedWalls,
      selectedAreaFillSymbols,
      selectedSvgs,
      selectedSmartTables = [],
      selectedBookShelves = [],
      selectedSquares = [],
      selectedCircles = [],
      selectedTriangles = [],
      selectedPolygons = [],
      selectedCircleStairs = [],
      selectedRectStairs = [],
      selectedTurnStairs = [],
      selectedDoors = [],
      selectedDoubleDoors = [],
    } = props;
    /** Wall/floor catalog products + wallpaper/flooring browse drawers. */
    const catalogMaterialsEnabled = true;
    const productAssetsEnabled = useFeatureFlag("floorplannerProduct3DAssets");
    const threeDSourcePanelEnabled = is3DSourcePanelEnabled();
    const [drawerIntent, setDrawerIntent] = React.useState<
      | "surface"
      | "wallpaper"
      | "flooring"
      | "product"
      | "browseMaterial"
      | "userModel"
      | null
    >(null);
    /**
     * When set, ProductsPanel opens on the texture-image step for this product
     * (preview-card click with multiple images).
     */
    const [texturePickSeedProduct, setTexturePickSeedProduct] =
      React.useState<CatalogImageOwner | null>(null);
    /** Category filter for the Ready materials grid (`null` = all). */
    const [materialCategoryFilter, setMaterialCategoryFilter] = React.useState<
      string | null
    >(null);
    const [catalogProductOpen, setCatalogProductOpen] = React.useState(false);
    /** Product material (catalog surface) section; collapsible like Fill / Line. */
    const [catalogMaterialOpen, setCatalogMaterialOpen] = React.useState(true);
    /** Standard / built-in materials section (incl. material parts). */
    const [materialsOpen, setMaterialsOpen] = React.useState(true);
    /**
     * Wallpaper / wall texture tile width↔height lock. Default locked so
     * changing one metre size scales the other and avoids pattern stretch.
     */
    const [wallPatternAspectLocked, setWallPatternAspectLocked] =
      React.useState(true);
    /** Albedo pixel size for linking while height is still "auto". */
    const [wallPatternImageSize, setWallPatternImageSize] = React.useState<{
      width: number;
      height: number;
    } | null>(null);
    const [userModelPanelPosition, setUserModelPanelPosition] = React.useState<{
      top: number;
      left: number;
    }>();
    const userModelButtonRef = React.useRef<HTMLButtonElement>(null);
    const userModelPanelRef = React.useRef<HTMLDivElement>(null);
    const materialColorUndoStartedRef = React.useRef(false);
    const [activeSlot, setActiveSlot] =
      React.useState<FloorPlan3DMaterialSlot | null>(null);
    /** Which wall face catalog/Ready materials target when side finishes are on. */
    const [wallMaterialSide, setWallMaterialSide] =
      React.useState<WallFaceSide>("left");
    /**
     * After assigning any material in 2D (walls, floors, shapes, product slots),
     * offer Switch to 3D / Stay in 2D so the finish can be previewed.
     */
    const [showMaterial3DPreviewPrompt, setShowMaterial3DPreviewPrompt] =
      React.useState(false);

    const maybePromptMaterial3DPreview = () => {
      if (editorStore.view3D) return;
      setShowMaterial3DPreviewPrompt(true);
    };

    const dismissMaterial3DPreviewPrompt = () => {
      setShowMaterial3DPreviewPrompt(false);
    };

    const switchTo3DToPreviewMaterials = () => {
      setShowMaterial3DPreviewPrompt(false);
      if (
        floorplannerStore.planJsonSizeLevel === "critical" &&
        !window.confirm(t("planSize.confirm3d"))
      ) {
        return;
      }
      editorStore.setView3D(true);
    };
    const surfaceTargetCount =
      selectedWalls.length + selectedAreaFillSymbols.length;
    const productTargetCount = selectedSvgs.length;
    const floorplannerSymbolDefaultKeys = React.useMemo(
      () =>
        Array.from(
          new Set(
            selectedSvgs
              .map(getFloorplannerSymbol3DDefaultKey)
              .filter((key): key is string => Boolean(key)),
          ),
        ).sort(),
      [selectedSvgs],
    );
    const {
      defaultsByKey: floorplannerSymbolDefaultsByKey,
      defaultProductAssetsByProductId,
      signature: floorplannerSymbolDefaultsSignature,
    } = useFloorplannerSymbol3DDefaults(
      floorplannerSymbolDefaultKeys,
      threeDSourcePanelEnabled && productTargetCount > 0,
    );
    useFloorplannerBuiltInMaterialSurfaces(true);
    const effectiveSelectedSvgs = React.useMemo(
      () =>
        applyFloorplannerSymbol3DDefaults(
          selectedSvgs,
          floorplannerSymbolDefaultsByKey,
          defaultProductAssetsByProductId,
        ) as SvgType[],
      [
        defaultProductAssetsByProductId,
        floorplannerSymbolDefaultsByKey,
        floorplannerSymbolDefaultsSignature,
        selectedSvgs,
      ],
    );
    const selectedProductIdsKey = selectedSvgs.map((svg) => svg.id).join("|");

    React.useEffect(() => {
      setCatalogProductOpen(false);
    }, [selectedProductIdsKey]);

    const show3DSourceControls =
      threeDSourcePanelEnabled && productTargetCount > 0;
    // Materials only for library Symbols / shapes — not catalog or uploads.
    const librarySvgForMaterials =
      selectedSvgs.length === 1 && !usesExternalOrUploaded3D(selectedSvgs[0])
        ? selectedSvgs[0]
        : null;
    const smartMaterialTargets = [
      ...selectedSmartTables,
      ...selectedBookShelves,
    ].filter((symbol) => !usesExternalOrUploaded3D(symbol));
    const smartMaterialModelKeys = new Set(
      smartMaterialTargets.map(
        (symbol) => symbol.threeD?.modelKey ?? symbol.libraryKey,
      ),
    );
    const showSmartMaterialControls =
      smartMaterialTargets.length > 0 && smartMaterialModelKeys.size === 1;
    const selectedStairSymbols: StairMaterialTarget[] = [
      ...selectedCircleStairs,
      ...selectedRectStairs,
      ...selectedTurnStairs,
    ].filter((symbol) => !usesExternalOrUploaded3D(symbol));
    const selectedDoorSymbols: DoorMaterialTarget[] = [
      ...selectedDoors,
      ...selectedDoubleDoors,
    ].filter((symbol) => !usesExternalOrUploaded3D(symbol));
    const selectedShapeSymbols: ShapeMaterialTarget[] = [
      ...selectedSquares,
      ...selectedCircles,
      ...selectedTriangles,
      ...selectedPolygons,
    ];
    const primitiveModelKey = showSmartMaterialControls
      ? (smartMaterialTargets[0].threeD?.modelKey ??
        smartMaterialTargets[0].libraryKey)
      : librarySvgForMaterials
        ? (librarySvgForMaterials.threeD?.modelKey ??
          librarySvgForMaterials.libraryKey)
        : undefined;
    const doorsOnlySelection =
      selectedDoorSymbols.length > 0 &&
      !librarySvgForMaterials &&
      selectedShapeSymbols.length === 0 &&
      selectedStairSymbols.length === 0 &&
      !showSmartMaterialControls &&
      surfaceTargetCount === 0;
    const productMaterialOptions: ProductMaterialOptionsConfig | null =
      showSmartMaterialControls || librarySvgForMaterials
        ? getProductMaterialOptions({
            kind: "primitive",
            modelKey: primitiveModelKey,
          })
        : doorsOnlySelection
          ? getProductMaterialOptions({ kind: "door" })
          : selectedStairSymbols.length === 1 &&
              !librarySvgForMaterials &&
              selectedShapeSymbols.length === 0 &&
              !showSmartMaterialControls
            ? getProductMaterialOptions({ kind: "stairs" })
            : selectedShapeSymbols.length === 1 &&
                !librarySvgForMaterials &&
                selectedStairSymbols.length === 0 &&
                !showSmartMaterialControls
              ? getProductMaterialOptions({ kind: "shape" })
              : null;
    const registryMaterialSlots = showSmartMaterialControls
      ? getPrimitiveSymbolMaterialSlots(primitiveModelKey)
      : librarySvgForMaterials
        ? getPrimitiveSymbolMaterialSlots(primitiveModelKey)
        : [];
    const materialSlots = productMaterialOptions
      ? listProductMaterialOptionSlots(productMaterialOptions)
      : registryMaterialSlots;
    const showDoorMaterialControls =
      doorsOnlySelection && materialSlots.length > 0;
    const showStairsMaterialControls =
      !showSmartMaterialControls &&
      !librarySvgForMaterials &&
      !showDoorMaterialControls &&
      selectedShapeSymbols.length === 0 &&
      selectedStairSymbols.length === 1 &&
      materialSlots.length > 0;
    const showFurnitureMaterialControls =
      !showSmartMaterialControls &&
      Boolean(librarySvgForMaterials) &&
      materialSlots.length > 0;
    const showShapeMaterialControls =
      !showSmartMaterialControls &&
      !showFurnitureMaterialControls &&
      !showStairsMaterialControls &&
      !showDoorMaterialControls &&
      !librarySvgForMaterials &&
      selectedStairSymbols.length === 0 &&
      selectedShapeSymbols.length === 1;
    /** Walls / area fills: single Ready material via threeD.surfaceRef. */
    const showWallFloorMaterialControls =
      !showSmartMaterialControls &&
      !showFurnitureMaterialControls &&
      !showStairsMaterialControls &&
      !showDoorMaterialControls &&
      !showShapeMaterialControls &&
      !librarySvgForMaterials &&
      surfaceTargetCount > 0;
    /** Shapes, walls, and area fills share the no-part-tabs Ready picker. */
    const showDirectSurfaceMaterialControls =
      showShapeMaterialControls || showWallFloorMaterialControls;
    // Ready / built-in material picker (furniture parts, doors, walls, floors, shapes).
    const showBuiltInMaterialControls =
      ((showSmartMaterialControls ||
        showFurnitureMaterialControls ||
        showStairsMaterialControls ||
        showDoorMaterialControls) &&
        materialSlots.length > 0) ||
      showDirectSurfaceMaterialControls;
    /**
     * Catalog Product material panel for walls/floors. Hidden when Standard
     * material already covers the same surface so tiling / wallpaper browse
     * are not shown twice.
     */
    const showSurfaceControls =
      catalogMaterialsEnabled &&
      surfaceTargetCount > 0 &&
      !(showBuiltInMaterialControls && showWallFloorMaterialControls);
    /** Catalog wallpaper/flooring browse lives inside Standard material. */
    const showCatalogBrowseInStandardMaterials =
      catalogMaterialsEnabled &&
      showBuiltInMaterialControls &&
      showWallFloorMaterialControls;
    const skipCatalogSurfaceProductPreview =
      !showCatalogBrowseInStandardMaterials;
    const isCatalogSurfaceProductPreviewWallpaper = selectedWalls.length > 0;
    const catalogSurfaceProductCategoriesQuery =
      useFloorplannerCategoriesMeta(skipCatalogSurfaceProductPreview);
    const catalogSurfaceProductCategoriesMeta =
      catalogSurfaceProductCategoriesQuery?.data?.categoriesMeta;
    const wallpaperCategoryIds = React.useMemo(
      () => findWallpaperCategoryIds(catalogSurfaceProductCategoriesMeta),
      [catalogSurfaceProductCategoriesMeta],
    );
    const flooringCategoryIds = React.useMemo(
      () => findFlooringCategoryIds(catalogSurfaceProductCategoriesMeta),
      [catalogSurfaceProductCategoriesMeta],
    );
    const catalogSurfaceProductCategoryIds =
      isCatalogSurfaceProductPreviewWallpaper
        ? wallpaperCategoryIds
        : flooringCategoryIds;
    const catalogSurfaceProductCategoriesReady =
      !skipCatalogSurfaceProductPreview &&
      !catalogSurfaceProductCategoriesQuery?.loading &&
      catalogSurfaceProductCategoryIds.length > 0;
    const catalogSurfaceProductPreviewVariables = React.useMemo(
      () => ({
        // Fetch enough for the category-chip grid (Wallpaper/Flooring next to Stone).
        limit: MATERIAL_PANEL_PREVIEW_LIMIT,
        skip: 0,
        sortBy: "name" as const,
        categoryIds:
          catalogSurfaceProductCategoryIds.length > 0
            ? catalogSurfaceProductCategoryIds
            : undefined,
      }),
      [catalogSurfaceProductCategoryIds],
    );
    const catalogSurfaceProductPreviewQuery = useFloorplannerProducts(
      catalogSurfaceProductPreviewVariables,
      skipCatalogSurfaceProductPreview ||
        !catalogSurfaceProductCategoriesReady,
    );
    const catalogSurfaceProductPreviews =
      (catalogSurfaceProductPreviewQuery.data?.productsMeta?.data ??
        []) as CatalogImageOwner[];
    /**
     * Wallpaper shortcuts under Browse: used-in-plan first, then catalog fill.
     * Flooring keeps catalog name-sort only. Live MobX scan (observer).
     */
    const catalogSurfaceProductPreviewCards = (() => {
      const limit = CATALOG_SURFACE_PRODUCT_PREVIEW_LIMIT;
      if (!isCatalogSurfaceProductPreviewWallpaper) {
        return catalogSurfaceProductPreviews.slice(0, limit);
      }
      const usedOwners = collectUsedWallpapersFromPlan()
        .slice(0, limit)
        .map(usedWallpaperToCatalogOwner);
      if (usedOwners.length >= limit) {
        return usedOwners;
      }
      const usedIds = new Set(usedOwners.map((product) => product.id));
      const fillers = catalogSurfaceProductPreviews.filter(
        (product) => !usedIds.has(product.id),
      );
      return [...usedOwners, ...fillers].slice(0, limit);
    })();
    const materialSlotsKey = materialSlots.join("|");

    React.useEffect(() => {
      if (materialSlots.length === 0) {
        setActiveSlot(null);
        return;
      }
      setActiveSlot((current) =>
        current && materialSlots.includes(current) ? current : materialSlots[0],
      );
    }, [materialSlotsKey]);

    // Keep category filter when switching parts; reset only when leaving Materials.
    React.useEffect(() => {
      if (!showBuiltInMaterialControls) {
        setMaterialCategoryFilter(null);
      }
    }, [showBuiltInMaterialControls]);

    /**
     * Probe wallpaper albedo pixel size before any early return so locked
     * width/height linking can use image aspect while height is still "auto".
     */
    const wallPatternAspectImageUrlForProbe = (() => {
      if (selectedWalls.length === 0) return undefined;
      const liveWall =
        floorplannerStore.wallsMap.get(selectedWalls[0].id) ??
        selectedWalls[0];
      // Face-aware: whole-wall ref normally, the targeted side in per-side mode.
      const liveSurfaceRef = resolveWallFaceSurfaceRef(
        liveWall,
        wallMaterialSide,
      );
      const fromRef = liveSurfaceRef?.imageUrl;
      if (fromRef) return fromRef;
      const materialId =
        liveSurfaceRef?.id ??
        (wallUsesDifferentSideColors(liveWall)
          ? undefined
          : liveWall.threeD?.materialId);
      if (!materialId) return undefined;
      return (
        getBuiltInMaterialSurfaceMapping(materialId)?.imageUrl ??
        getBuiltInFurnitureMaterial(materialId)?.textureUrl
      );
    })();
    React.useEffect(() => {
      if (!wallPatternAspectImageUrlForProbe) {
        setWallPatternImageSize(null);
        return;
      }
      let cancelled = false;
      const image = new Image();
      image.onload = () => {
        if (cancelled) return;
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        setWallPatternImageSize(
          width > 0 && height > 0 ? { width, height } : null,
        );
      };
      image.onerror = () => {
        if (!cancelled) setWallPatternImageSize(null);
      };
      // No crossOrigin: we only need naturalWidth/Height, not pixel reads.
      image.src = getWallFloorCatalogImageUrl(wallPatternAspectImageUrlForProbe);
      return () => {
        cancelled = true;
      };
    }, [wallPatternAspectImageUrlForProbe]);

    if (
      !showSurfaceControls &&
      !show3DSourceControls &&
      !showBuiltInMaterialControls
    ) {
      return null;
    }

    // Always re-read from the store so clear/reset is visible even when the
    // property-panel memo still holds a pre-clear wall/symbol snapshot.
    const liveSelectedWalls = selectedWalls.map(
      (selected) => floorplannerStore.wallsMap.get(selected.id) ?? selected,
    );
    const liveSelectedAreaFillSymbols = selectedAreaFillSymbols.map(
      (selected) =>
        (floorplannerStore.symbolsMap.get(selected.id) as
          | AreaFillSymbolType
          | undefined) ?? selected,
    );
    const soleWallForSideMaterials =
      liveSelectedWalls.length === 1 &&
      liveSelectedWalls[0]?.lineForm !== "arc" &&
      wallUsesDifferentSideColors(liveSelectedWalls[0])
        ? liveSelectedWalls[0]
        : undefined;
    const wallMaterialTargetSide = soleWallForSideMaterials
      ? wallMaterialSide
      : null;

    const firstSurfaceRef = soleWallForSideMaterials
      ? resolveWallFaceSurfaceRef(soleWallForSideMaterials, wallMaterialSide)
      : (liveSelectedWalls[0]?.threeD?.surfaceRef ??
        liveSelectedAreaFillSymbols[0]?.threeD?.surfaceRef);

    /**
     * Write a catalog/Ready surface onto one wall face or the whole wall.
     * Assigning a material clears that face's fill color (paint↔material are
     * exclusive); clearing a material leaves fill color alone.
     */
    const setWallSurfaceThreeD = (
      wall: WallType,
      surfaceRef: CatalogSurfaceReference | null,
      options?: { materialId?: string | null },
    ): WallType["threeD"] => {
      if (wallMaterialTargetSide) {
        // Whole-wall surfaceRef / materialId always mirror Side 1 so clients
        // without per-side support still render a sensible finish.
        return withSideOneMirror(
          upsertWallFaceMaterial(wall, wallMaterialTargetSide, {
            surfaceRef,
            ...(surfaceRef ? { fillColor: null } : {}),
          }),
        );
      }
      const cleared = clearWallFaceMaterials(wall);
      if (surfaceRef === null) {
        return omitWallSurfaceMaterial(cleared);
      }
      if (options?.materialId === null) {
        const { materialId: _removed, ...rest } = cleared;
        return { ...rest, surfaceRef };
      }
      return {
        ...cleared,
        ...(options?.materialId !== undefined
          ? { materialId: options.materialId }
          : {}),
        surfaceRef,
      };
    };

    /**
     * Apply a wall material without touching fillColor so "Remove material"
     * can reveal the previous paint (classic plaster or user colour).
     * Side 2 only clears that face's fill override via setWallSurfaceThreeD.
     */
    const applyWallSurfaceMaterial = (
      wall: WallType,
      surfaceRef: CatalogSurfaceReference,
      options?: { materialId?: string | null },
    ) => {
      floorplannerStore.setObjectProperty(
        wall.id,
        "threeD",
        setWallSurfaceThreeD(wall, surfaceRef, options),
      );
    };
    const dictionaryEntry =
      firstSurfaceRef?.source === "surface"
        ? floorplannerStore.catalogDictionary.surfacesById[firstSurfaceRef.id]
        : firstSurfaceRef?.source === "material"
          ? floorplannerStore.catalogDictionary.materialsById[
              firstSurfaceRef.id
            ]
          : firstSurfaceRef?.source === "product"
            ? floorplannerStore.catalogDictionary.productsById[
                firstSurfaceRef.id
              ]
            : undefined;
    const firstProductRef = selectedSvgs[0]?.threeD?.product;
    const firstUserModelRef = selectedSvgs[0]?.threeD?.userModel;
    const firstSymbolSourceMode = selectedSvgs[0]?.threeD?.sourceMode;
    const productEntry = firstProductRef?.productId
      ? floorplannerStore.catalogDictionary.productsById[
          firstProductRef.productId
        ]
      : undefined;
    const materialAssignmentTarget = showSmartMaterialControls
      ? smartMaterialTargets[0]
      : showDoorMaterialControls
        ? selectedDoorSymbols[0]
        : showStairsMaterialControls
          ? selectedStairSymbols[0]
          : librarySvgForMaterials;
    const activeSlotAssignment = activeSlot
      ? materialAssignmentTarget?.threeD?.materialAssignments?.[activeSlot]
      : undefined;
    const activeProductOptionKey =
      productMaterialOptions && activeSlot
        ? getProductMaterialOptionKeyForSlot(productMaterialOptions, activeSlot)
        : undefined;
    const builtInCategoryGroups = showBuiltInMaterialControls
      ? listVisibleBuiltInFurnitureMaterialsByCategory()
      : [];
    const builtInPresetList = showBuiltInMaterialControls
      ? listVisibleBuiltInFurnitureMaterials()
      : [];
    const selectedBuiltInMaterialId = showShapeMaterialControls
      ? selectedShapeSymbols[0]?.threeD?.surfaceRef?.id ??
        selectedShapeSymbols[0]?.threeD?.materialAssignments?.wall?.materialId
      : showWallFloorMaterialControls
        ? liveSelectedWalls.length > 0
          ? (firstSurfaceRef?.id ??
            (soleWallForSideMaterials
              ? undefined
              : liveSelectedWalls[0]?.threeD?.materialId))
          : liveSelectedAreaFillSymbols[0]?.threeD?.surfaceRef?.id
        : activeSlotAssignment?.materialId ??
          activeSlotAssignment?.surfaceRef?.id;
    const selectedBuiltInMaterial = getBuiltInFurnitureMaterial(
      selectedBuiltInMaterialId,
    );
    const catalogMaterialCategoryKey = showCatalogBrowseInStandardMaterials
      ? selectedWalls.length > 0
        ? CATALOG_MATERIAL_CATEGORY_WALLPAPER
        : CATALOG_MATERIAL_CATEGORY_FLOORING
      : null;
    const catalogMaterialCategoryLabel = catalogMaterialCategoryKey
      ? selectedWalls.length > 0
        ? t("propertyPanel.materialCategoryWallpaper")
        : t("propertyPanel.materialCategoryFlooring")
      : null;
    const showingCatalogMaterialCategory = isCatalogMaterialCategory(
      materialCategoryFilter,
    );
    const filteredBuiltInMaterials =
      materialCategoryFilter && !showingCatalogMaterialCategory
        ? builtInPresetList.filter(
            (material) =>
              getBuiltInMaterialCategoryKey(material) === materialCategoryFilter,
          )
        : builtInPresetList;
    // "All" shows one sample per category so the grid is varied, not 9 metals.
    const previewBuiltInMaterials = showingCatalogMaterialCategory
      ? []
      : materialCategoryFilter
        ? filteredBuiltInMaterials.slice(0, MATERIAL_PANEL_PREVIEW_LIMIT)
        : builtInCategoryGroups
            .map((group) => group.preview)
            .slice(0, MATERIAL_PANEL_PREVIEW_LIMIT);
    const previewCatalogMaterials = showingCatalogMaterialCategory
      ? isCatalogSurfaceProductPreviewWallpaper
        ? (() => {
            const usedOwners = collectUsedWallpapersFromPlan()
              .slice(0, MATERIAL_PANEL_PREVIEW_LIMIT)
              .map(usedWallpaperToCatalogOwner);
            const usedIds = new Set(usedOwners.map((product) => product.id));
            const fillers = catalogSurfaceProductPreviews.filter(
              (product) => !usedIds.has(product.id),
            );
            return [...usedOwners, ...fillers].slice(
              0,
              MATERIAL_PANEL_PREVIEW_LIMIT,
            );
          })()
        : catalogSurfaceProductPreviews.slice(0, MATERIAL_PANEL_PREVIEW_LIMIT)
      : [];
    const hasMoreBuiltInMaterials = showingCatalogMaterialCategory
      ? true
      : filteredBuiltInMaterials.length > previewBuiltInMaterials.length;
    const showMaterialPicker =
      showBuiltInMaterialControls &&
      (showDirectSurfaceMaterialControls || Boolean(activeSlot));
    const activeSlotEntry = activeSlotAssignment?.surfaceRef
      ? activeSlotAssignment.surfaceRef.source === "surface"
        ? floorplannerStore.catalogDictionary.surfacesById[
            activeSlotAssignment.surfaceRef.id
          ]
        : activeSlotAssignment.surfaceRef.source === "product"
          ? floorplannerStore.catalogDictionary.productsById[
              activeSlotAssignment.surfaceRef.id
            ]
          : floorplannerStore.catalogDictionary.materialsById[
              activeSlotAssignment.surfaceRef.id
            ]
      : undefined;
    const editableMaterialSurfaceRef = showShapeMaterialControls
      ? selectedShapeSymbols[0]?.threeD?.surfaceRef ??
        selectedShapeSymbols[0]?.threeD?.materialAssignments?.wall?.surfaceRef
      : showWallFloorMaterialControls
        ? liveSelectedWalls.length > 0
          ? firstSurfaceRef
          : liveSelectedAreaFillSymbols[0]?.threeD?.surfaceRef
        : activeSlotAssignment?.surfaceRef;
    const editableMaterialId = showShapeMaterialControls
      ? selectedShapeSymbols[0]?.threeD?.materialAssignments?.wall?.materialId ??
        selectedShapeSymbols[0]?.threeD?.surfaceRef?.id
      : showWallFloorMaterialControls
        ? liveSelectedWalls.length > 0
          ? (firstSurfaceRef?.id ??
            (soleWallForSideMaterials
              ? undefined
              : liveSelectedWalls[0]?.threeD?.materialId))
          : liveSelectedAreaFillSymbols[0]?.threeD?.surfaceRef?.id
        : activeSlotAssignment?.materialId ??
          activeSlotAssignment?.surfaceRef?.id;
    const canEditMaterialColor =
      showBuiltInMaterialControls &&
      (showDirectSurfaceMaterialControls || Boolean(activeSlot)) &&
      canEditSolidBuiltInMaterialColor(
        editableMaterialId,
        editableMaterialSurfaceRef,
      );
    const editableMaterialColor =
      editableMaterialSurfaceRef?.color ??
      getBuiltInFurnitureMaterial(editableMaterialId)?.color;
    const selectedBuiltInTextureUrl =
      (selectedBuiltInMaterial
        ? getBuiltInMaterialSurfaceMapping(selectedBuiltInMaterial.id)
            ?.imageUrl ?? selectedBuiltInMaterial.textureUrl
        : undefined) ?? editableMaterialSurfaceRef?.imageUrl;
    const canEditMaterialTextureMapping = Boolean(
      showMaterialPicker && selectedBuiltInTextureUrl,
    );
    const editableMaterialRepeatScale =
      editableMaterialSurfaceRef?.repeatScale ??
      selectedBuiltInMaterial?.repeatScale ??
      1;
    /**
     * Pattern size is the inverse of UV repeat so larger numbers read as a
     * bigger grain (stored still as surfaceRef.repeatScale for Three.js).
     * Walls use metre-space UVs, so the same control is "repeat every X m".
     */
    const wallSurfaceSelected = selectedWalls.length > 0;
    const editableMaterialPatternSize = wallSurfaceSelected
      ? Number(
          (
            wallTextureTileMetersFromRepeatScale(
              editableMaterialSurfaceRef?.repeatScale ??
                selectedBuiltInMaterial?.repeatScale,
            ) ?? 4
          ).toFixed(2),
        )
      : Number((1 / Math.max(editableMaterialRepeatScale, 0.01)).toFixed(2));
    const wallCatalogTileMeters = Number(
      (
        wallTextureTileMetersFromRepeatScale(firstSurfaceRef?.repeatScale) ?? 4
      ).toFixed(2),
    );
    /** Explicit vertical tile size; empty = auto from image aspect. */
    const wallCatalogTileHeightMeters = (() => {
      const meters = wallTextureTileMetersFromRepeatScale(
        firstSurfaceRef?.repeatScaleY,
        null,
      );
      return meters === null ? ("" as const) : Number(meters.toFixed(2));
    })();
    const editableWallTileHeightMeters = (() => {
      const meters = wallTextureTileMetersFromRepeatScale(
        editableMaterialSurfaceRef?.repeatScaleY,
        null,
      );
      return meters === null ? ("" as const) : Number(meters.toFixed(2));
    })();
    /**
     * Build repeatScale / repeatScaleY overrides for wall tile edits.
     * When aspect is locked, the partner axis scales too — using explicit
     * height when set, otherwise albedo pixel aspect while height is auto.
     */
    const wallTileMappingFromLinkedEdit = (
      changed: "width" | "height",
      nextMeters: number,
      currentWidthMeters: number,
      currentHeightMeters: number | "",
    ): Partial<{ repeatScale: number; repeatScaleY: number }> => {
      const linked =
        wallPatternAspectLocked &&
        linkedWallTileMeters({
          changed,
          nextMeters,
          currentWidthMeters,
          currentHeightMeters:
            currentHeightMeters === "" ? null : currentHeightMeters,
          imageWidth: wallPatternImageSize?.width,
          imageHeight: wallPatternImageSize?.height,
        });
      if (linked) {
        return {
          repeatScale: wallTextureRepeatScaleFromTileMeters(
            Number(linked.widthMeters.toFixed(2)),
          ),
          repeatScaleY: wallTextureRepeatScaleFromTileMeters(
            Number(linked.heightMeters.toFixed(2)),
          ),
        };
      }
      if (changed === "width") {
        return {
          repeatScale: wallTextureRepeatScaleFromTileMeters(nextMeters),
        };
      }
      return {
        repeatScaleY: wallTextureRepeatScaleFromTileMeters(nextMeters),
      };
    };
    const firstSelectedWallHeight = wallSurfaceSelected
      ? getWall3DHeight(liveSelectedWalls[0])
      : undefined;
    /** Degrees in the UI; surfaceRef.rotation stays radians for Three.js. */
    const editableMaterialRotationDegrees = Math.round(
      ((editableMaterialSurfaceRef?.rotation ?? 0) * 180) / Math.PI,
    );
    const currentSurfaceLabel =
      getCatalogEntryDisplayName(dictionaryEntry, firstSurfaceRef?.id) ??
      t("propertyPanel.defaultMaterial");
    const currentSurfaceImageUrl =
      firstSurfaceRef?.imageUrl ?? dictionaryEntry?.imageUrl;
    const currentSurfaceSubtitle =
      dictionaryEntry?.brand ??
      dictionaryEntry?.categories
        ?.map((category) => category.name)
        .find(Boolean) ??
      undefined;
    const currentProductLabel =
      getCatalogEntryDisplayName(productEntry, firstProductRef?.productId) ??
      firstProductRef?.name ??
      t("propertyPanel.primitive");
    const closeCatalogDrawer = () => {
      setDrawerIntent(null);
      setTexturePickSeedProduct(null);
    };

    const openProductMaterialBrowser = () => {
      setTexturePickSeedProduct(null);
      if (selectedWalls.length > 0) {
        setDrawerIntent("wallpaper");
        return;
      }
      if (selectedAreaFillSymbols.length > 0) {
        setDrawerIntent("flooring");
        return;
      }
      setDrawerIntent("surface");
    };

    const productMaterialBrowseLabel =
      selectedWalls.length > 0
        ? t("propertyPanel.browseWallpaper")
        : selectedAreaFillSymbols.length > 0
          ? t("propertyPanel.browseFlooring")
          : t("common.browse");
    const selectedSymbolKey =
      selectedSvgs[0]?.threeD?.modelKey ?? selectedSvgs[0]?.libraryKey;
    const productSearchText =
      getSearchTextForPrimitiveSymbol(selectedSymbolKey);
    const surfaceDefaultSlot: FloorPlan3DMaterialSlot =
      selectedWalls.length > 0 ? "wall" : "floor";

    const applySurface = (surface: CatalogImageOwner) => {
      const imageUrl = surface.images?.[0]
        ? getWallFloorCatalogImageUrl(surface.images[0])
        : undefined;
      const color = parseColor(surface.colors?.[0]?.hex);
      const surfaceRef = {
        source: "surface" as const,
        id: surface.id,
        imageUrl,
        color,
      };
      floorplannerStore.beginUndoTransaction("Assign catalog surface");
      selectedWalls.forEach((selected) => {
        const wall =
          floorplannerStore.wallsMap.get(selected.id) ?? selected;
        floorplannerStore.recordWallForUndo(wall.id);
        applyWallSurfaceMaterial(wall, surfaceRef, { materialId: null });
        floorplannerStore.recordWallChange(wall.id, "modify");
      });
      selectedAreaFillSymbols.forEach((selected) => {
        const symbol =
          floorplannerStore.symbolsMap.get(selected.id) ?? selected;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          surfaceRef,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.upsertCatalogDictionaryEntry("surface", surface.id, {
        id: surface.id,
        name: getCatalogItemDisplayName(surface),
        description: surface.description ?? undefined,
        imageUrl,
        categories: getCatalogCategorySnapshots(surface),
        colors: surface.colors?.filter(Boolean).map((surfaceColor) => ({
          id: surfaceColor?.id ?? undefined,
          name: surfaceColor?.name ?? undefined,
          hex: surfaceColor?.hex ?? undefined,
        })),
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: surfaceTargetCount,
        targetType: selectedWalls.length > 0 ? "wall" : "areaFill",
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
      setDrawerIntent(null);
      maybePromptMaterial3DPreview();
    };

    /**
     * Apply a sellable Wallpaper catalog product to selected walls.
     * Uses the chosen texture image (or MATERIAL bake / images[0] default),
     * resized ≤512, and registers the product on the Product List.
     */
    const applyWallpaperProduct = (
      product: CatalogImageOwner,
      options?: { textureImageUrl?: string },
    ) => {
      if (selectedWalls.length === 0) return;
      const imageUrl = options?.textureImageUrl?.trim()
        ? getWallFloorCatalogImageUrl(options.textureImageUrl.trim())
        : getWallFloorProductTextureUrl(product);
      if (!imageUrl) return;
      const displayName = getCatalogItemDisplayName(product);
      const surfaceRef: CatalogSurfaceReference = {
        source: "product",
        id: product.id,
        imageUrl,
        color: 0xffffff,
        repeatScale: wallTextureRepeatScaleFromTileMeters(
          DEFAULT_WALL_TEXTURE_TILE_METERS,
        ),
        roughness: 0.78,
        metalness: 0,
      };

      floorplannerStore.beginUndoTransaction("Assign wallpaper product");
      selectedWalls.forEach((selected) => {
        const wall = floorplannerStore.wallsMap.get(selected.id) ?? selected;
        floorplannerStore.recordWallForUndo(wall.id);
        applyWallSurfaceMaterial(wall, surfaceRef, { materialId: null });
        floorplannerStore.recordWallChange(wall.id, "modify");
      });
      floorplannerStore.upsertCatalogDictionaryEntry("product", product.id, {
        id: product.id,
        name: displayName,
        description: product.description ?? undefined,
        imageUrl,
        brand: product.brand ?? undefined,
        supplierName: product.supplier?.name ?? undefined,
        price:
          typeof product.price === "number" && Number.isFinite(product.price)
            ? product.price
            : undefined,
        categories: getCatalogCategorySnapshots(product),
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: selectedWalls.length,
        targetType: "wall",
        source: "wallpaperProduct",
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
      closeCatalogDrawer();
      maybePromptMaterial3DPreview();
    };

    /**
     * Apply a sellable Flooring catalog product to selected area fills.
     * Uses the chosen texture image (or MATERIAL bake / images[0] default),
     * resized ≤512, and registers the product on the Product List.
     */
    const applyFlooringProduct = (
      product: CatalogImageOwner,
      options?: { textureImageUrl?: string },
    ) => {
      if (selectedAreaFillSymbols.length === 0) return;
      const imageUrl = options?.textureImageUrl?.trim()
        ? getWallFloorCatalogImageUrl(options.textureImageUrl.trim())
        : getWallFloorProductTextureUrl(product);
      if (!imageUrl) return;
      const displayName = getCatalogItemDisplayName(product);
      const surfaceRef: CatalogSurfaceReference = {
        source: "product",
        id: product.id,
        imageUrl,
        color: 0xffffff,
        repeatScale: 1,
        roughness: 0.78,
        metalness: 0,
      };

      floorplannerStore.beginUndoTransaction("Assign flooring product");
      selectedAreaFillSymbols.forEach((selected) => {
        const symbol =
          floorplannerStore.symbolsMap.get(selected.id) ?? selected;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...omitSymbolDirectSurfaceMaterial(symbol.threeD),
          surfaceRef,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.upsertCatalogDictionaryEntry("product", product.id, {
        id: product.id,
        name: displayName,
        description: product.description ?? undefined,
        imageUrl,
        brand: product.brand ?? undefined,
        supplierName: product.supplier?.name ?? undefined,
        price:
          typeof product.price === "number" && Number.isFinite(product.price)
            ? product.price
            : undefined,
        categories: getCatalogCategorySnapshots(product),
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: selectedAreaFillSymbols.length,
        targetType: "areaFill",
        source: "flooringProduct",
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
      closeCatalogDrawer();
      maybePromptMaterial3DPreview();
    };

    /**
     * Preview cards: apply immediately when only one texture image exists;
     * otherwise open the browser on the texture-pick step.
     */
    const beginCatalogSurfaceProductFromPreview = (
      product: CatalogImageOwner,
    ) => {
      const options = listWallFloorProductTextureImageOptions(product);
      if (options.length <= 1) {
        if (isCatalogSurfaceProductPreviewWallpaper) {
          applyWallpaperProduct(
            product,
            options[0] ? { textureImageUrl: options[0].rawUrl } : undefined,
          );
        } else {
          applyFlooringProduct(
            product,
            options[0] ? { textureImageUrl: options[0].rawUrl } : undefined,
          );
        }
        return;
      }
      setTexturePickSeedProduct(product);
      setDrawerIntent(
        isCatalogSurfaceProductPreviewWallpaper ? "wallpaper" : "flooring",
      );
    };

    const applyProduct = (product: CatalogImageOwner) => {
      const imageUrl = product.images?.[0]
        ? getResizedCatalogImageUrl(product.images[0])
        : undefined;
      const displayName = getCatalogItemDisplayName(product);
      const asset = productAssetsEnabled
        ? product.product3DAssets?.find(
            (candidate) => candidate.status === "ready" && candidate.glbUrl,
          )
        : undefined;
      const resolvedDimensions = resolvePublishedProduct3DDimensions(
        product,
        asset,
      );

      floorplannerStore.beginUndoTransaction("Assign catalog product");
      selectedSvgs.forEach((symbol) => {
        const proxyResolution = resolveProductProxyForSymbol(
          product,
          symbol.threeD?.modelKey ?? symbol.libraryKey,
        );
        floorplannerStore.recordSymbolForUndo(symbol.id);
        const nextDimensions = resolvedDimensions.dimensions;
        if (nextDimensions.width) {
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "svgLength",
            nextDimensions.width,
          );
        }
        if (nextDimensions.depth) {
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "svgHeight",
            nextDimensions.depth,
          );
        }
        if (nextDimensions.width && nextDimensions.depth) {
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "ratio",
            nextDimensions.width / nextDimensions.depth,
          );
        }
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: "custom",
          dimensions: nextDimensions,
          height: nextDimensions.height,
          product: {
            ...symbol.threeD?.product,
            productId: product.id,
            renderMode: asset ? "asset3D" : proxyResolution.renderMode,
            selectedImageUrl: imageUrl,
            asset,
            name: displayName,
            brand: product.brand ?? undefined,
            supplierName: product.supplier?.name ?? undefined,
            dimensions: nextDimensions,
            dimensionConfidence: resolvedDimensions.confidence,
            dimensionUnit: resolvedDimensions.unit,
            dimensionSource: resolvedDimensions.source,
            proxyModelKey: proxyResolution.proxyModelKey,
            fallbackReason: asset ? undefined : proxyResolution.fallbackReason,
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.upsertCatalogDictionaryEntry("product", product.id, {
        id: product.id,
        name: displayName,
        description: product.description ?? undefined,
        imageUrl,
        brand: product.brand ?? undefined,
        supplierName: product.supplier?.name ?? undefined,
        price:
          typeof product.price === "number" && Number.isFinite(product.price)
            ? product.price
            : undefined,
        categories: getCatalogCategorySnapshots(product),
      });
      trackFloorplan3DMetric("product_replacement", {
        targets: selectedSvgs.length,
        renderMode: asset ? "asset3D" : "proxy-or-preview",
        has3DAsset: Boolean(asset),
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
      setDrawerIntent(null);
    };

    const applyUserModel = (model: UserModel3DLibraryItem) => {
      if (model.status !== "ready" || !model.optimizedUrl) return;
      const dimensions = getUserModel3DInsertDimensions(model);
      floorplannerStore.beginUndoTransaction("Assign uploaded 3D object");
      selectedSvgs.forEach((symbol) => {
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "svgLength",
          dimensions.width,
        );
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "svgHeight",
          dimensions.depth,
        );
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "ratio",
          dimensions.width / Math.max(dimensions.depth, 0.001),
        );
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: "custom",
          height: dimensions.height,
          placementMode: symbol.threeD?.placementMode ?? "floor",
          collision: symbol.threeD?.collision ?? "box",
          dimensions: {
            ...symbol.threeD?.dimensions,
            height: dimensions.height,
          },
          userModel: {
            modelId: model.id,
            url: model.optimizedUrl,
            version: model.pipelineVersion ?? undefined,
            status: "ready",
            name: model.name,
            thumbnailUrl: model.thumbnailUrl ?? undefined,
            sourceImageUrl: model.sourceImageUrl ?? undefined,
            dimensions,
            bounds: model.bounds ?? undefined,
            sha256: model.sha256 ?? undefined,
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
      setDrawerIntent(null);
    };

    const resolveSlotMaterialTargets = () => {
      if (showSmartMaterialControls) return smartMaterialTargets;
      if (showDoorMaterialControls) return selectedDoorSymbols;
      if (showStairsMaterialControls) return selectedStairSymbols;
      return librarySvgForMaterials ? [librarySvgForMaterials] : [];
    };

    const applySlotSurface = (surface: CatalogImageOwner) => {
      if (!activeSlot) return;
      const imageUrl = surface.images?.[0]
        ? getResizedCatalogImageUrl(surface.images[0])
        : undefined;
      const color = parseColor(surface.colors?.[0]?.hex);
      const surfaceRef = {
        source: "surface" as const,
        id: surface.id,
        imageUrl,
        color,
      };

      floorplannerStore.beginUndoTransaction("Assign product material");
      const materialTargets = resolveSlotMaterialTargets();
      materialTargets.forEach((symbol) => {
        const materialAssignments = symbol.threeD?.materialAssignments ?? {};
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          materialAssignments: {
            ...materialAssignments,
            [activeSlot]: {
              ...materialAssignments[activeSlot],
              materialId: surface.id,
              productId: symbol.threeD?.product?.productId,
              surfaceRef,
            },
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.upsertCatalogDictionaryEntry("surface", surface.id, {
        id: surface.id,
        name: getCatalogItemDisplayName(surface),
        description: surface.description ?? undefined,
        imageUrl,
        categories: getCatalogCategorySnapshots(surface),
        colors: surface.colors?.filter(Boolean).map((surfaceColor) => ({
          id: surfaceColor?.id ?? undefined,
          name: surfaceColor?.name ?? undefined,
          hex: surfaceColor?.hex ?? undefined,
        })),
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: materialTargets.length,
        targetType: "productSlot",
        slot: activeSlot,
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
      setDrawerIntent(null);
      maybePromptMaterial3DPreview();
    };

    const applySlotPreset = (preset: BuiltInFurnitureMaterial) => {
      if (!activeSlot) return;
      const surfaceRef = builtInFurnitureMaterialToSurfaceRefWithOverlay(preset);
      floorplannerStore.beginUndoTransaction("Assign built-in material");
      const materialTargets = resolveSlotMaterialTargets();
      materialTargets.forEach((symbol) => {
        const materialAssignments = symbol.threeD?.materialAssignments ?? {};
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          materialAssignments: {
            ...materialAssignments,
            [activeSlot]: {
              ...materialAssignments[activeSlot],
              materialId: preset.id,
              surfaceRef,
            },
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: materialTargets.length,
        targetType: "builtInSlot",
        slot: activeSlot,
        materialId: preset.id,
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const clearSlotMaterial = () => {
      if (!activeSlot) return;
      floorplannerStore.beginUndoTransaction("Clear slot material");
      const materialTargets = resolveSlotMaterialTargets();
      materialTargets.forEach((symbol) => {
        const materialAssignments = {
          ...(symbol.threeD?.materialAssignments ?? {}),
        };
        delete materialAssignments[activeSlot];
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          materialAssignments:
            Object.keys(materialAssignments).length > 0
              ? materialAssignments
              : undefined,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const applyShapePreset = (preset: BuiltInFurnitureMaterial) => {
      const surfaceRef = builtInFurnitureMaterialToSurfaceRefWithOverlay(preset);
      floorplannerStore.beginUndoTransaction("Assign built-in material");
      selectedShapeSymbols.forEach((symbol) => {
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          surfaceRef,
          materialAssignments: {
            ...(symbol.threeD?.materialAssignments ?? {}),
            wall: {
              materialId: preset.id,
              surfaceRef,
            },
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: selectedShapeSymbols.length,
        targetType: "builtInShape",
        materialId: preset.id,
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    /** Ready materials on walls (surfaceRef + materialId) and area-fill floors. */
    const applyWallFloorPreset = (preset: BuiltInFurnitureMaterial) => {
      const surfaceRef = builtInFurnitureMaterialToSurfaceRefWithOverlay(preset);
      floorplannerStore.beginUndoTransaction("Assign built-in material");
      selectedWalls.forEach((selected) => {
        // Always read the live wall so switches replace the latest threeD.
        const wall =
          floorplannerStore.wallsMap.get(selected.id) ?? selected;
        floorplannerStore.recordWallForUndo(wall.id);
        if (wallMaterialTargetSide) {
          applyWallSurfaceMaterial(wall, surfaceRef);
        } else {
          floorplannerStore.setObjectProperty(wall.id, "threeD", {
            ...clearWallFaceMaterials(wall),
            materialId: preset.id,
            surfaceRef,
          });
        }
        floorplannerStore.recordWallChange(wall.id, "modify");
      });
      selectedAreaFillSymbols.forEach((selected) => {
        const symbol =
          floorplannerStore.symbolsMap.get(selected.id) ?? selected;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          surfaceRef,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      trackFloorplan3DMetric("material_assignment", {
        targets: surfaceTargetCount,
        targetType: selectedWalls.length > 0 ? "builtInWall" : "builtInAreaFill",
        materialId: preset.id,
      });
      monitorLongTaskAfterMaterialAssignment();
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const applyBuiltInMaterial = (preset: BuiltInFurnitureMaterial) => {
      if (showShapeMaterialControls) {
        applyShapePreset(preset);
      } else if (showWallFloorMaterialControls) {
        applyWallFloorPreset(preset);
      } else {
        applySlotPreset(preset);
      }
      maybePromptMaterial3DPreview();
    };

    const clearShapeMaterial = () => {
      floorplannerStore.beginUndoTransaction("Clear shape material");
      selectedShapeSymbols.forEach((symbol) => {
        const materialAssignments = {
          ...(symbol.threeD?.materialAssignments ?? {}),
        };
        delete materialAssignments.wall;
        const clearedThreeD = omitSymbolDirectSurfaceMaterial(symbol.threeD, {
          clearAssignments: true,
        });
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...clearedThreeD,
          ...(Object.keys(materialAssignments).length > 0
            ? { materialAssignments }
            : {}),
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const clearWallFloorMaterial = () => {
      floorplannerStore.beginUndoTransaction("Clear wall/floor material");
      selectedWalls.forEach((selected) => {
        const wall =
          floorplannerStore.wallsMap.get(selected.id) ?? selected;
        floorplannerStore.recordWallForUndo(wall.id);
        floorplannerStore.setObjectProperty(
          wall.id,
          "threeD",
          setWallSurfaceThreeD(wall, null, { materialId: null }),
        );
        floorplannerStore.recordWallChange(wall.id, "modify");
      });
      selectedAreaFillSymbols.forEach((selected) => {
        const symbol =
          floorplannerStore.symbolsMap.get(selected.id) ?? selected;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "threeD",
          omitSymbolDirectSurfaceMaterial(symbol.threeD),
        );
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    /**
     * Live-update solid built-in color on surfaceRef; one undo entry per drag.
     */
    const applyEditableMaterialColor = (nextColor: number) => {
      if (!canEditMaterialColor) return;

      if (!materialColorUndoStartedRef.current) {
        floorplannerStore.beginUndoTransaction("Edit material color");
        materialColorUndoStartedRef.current = true;
      }

      if (showShapeMaterialControls) {
        selectedShapeSymbols.forEach((symbol) => {
          const currentSurfaceRef =
            symbol.threeD?.surfaceRef ??
            symbol.threeD?.materialAssignments?.wall?.surfaceRef;
          if (!currentSurfaceRef) return;
          const surfaceRef: CatalogSurfaceReference = {
            ...currentSurfaceRef,
            color: nextColor,
          };
          floorplannerStore.recordSymbolForUndo(symbol.id);
          floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
            ...symbol.threeD,
            surfaceRef,
            materialAssignments: {
              ...(symbol.threeD?.materialAssignments ?? {}),
              wall: {
                ...(symbol.threeD?.materialAssignments?.wall ?? {
                  materialId: surfaceRef.id,
                }),
                surfaceRef,
              },
            },
          });
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
        });
        invalidate();
        return;
      }

      if (showWallFloorMaterialControls) {
        selectedWalls.forEach((selected) => {
          const wall =
            floorplannerStore.wallsMap.get(selected.id) ?? selected;
          const currentSurfaceRef = wallMaterialTargetSide
            ? resolveWallFaceSurfaceRef(wall, wallMaterialTargetSide)
            : wall.threeD?.surfaceRef;
          if (!currentSurfaceRef) return;
          const surfaceRef: CatalogSurfaceReference = {
            ...currentSurfaceRef,
            color: nextColor,
          };
          floorplannerStore.recordWallForUndo(wall.id);
          floorplannerStore.setObjectProperty(
            wall.id,
            "threeD",
            setWallSurfaceThreeD(wall, surfaceRef),
          );
          floorplannerStore.recordWallChange(wall.id, "modify");
        });
        selectedAreaFillSymbols.forEach((selected) => {
          const symbol =
            floorplannerStore.symbolsMap.get(selected.id) ?? selected;
          const currentSurfaceRef = symbol.threeD?.surfaceRef;
          if (!currentSurfaceRef) return;
          const surfaceRef: CatalogSurfaceReference = {
            ...currentSurfaceRef,
            color: nextColor,
          };
          floorplannerStore.recordSymbolForUndo(symbol.id);
          floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
            ...symbol.threeD,
            surfaceRef,
          });
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
        });
        invalidate();
        return;
      }

      if (!activeSlot) return;
      const materialTargets = resolveSlotMaterialTargets();
      materialTargets.forEach((symbol) => {
        const materialAssignments = symbol.threeD?.materialAssignments ?? {};
        const currentAssignment = materialAssignments[activeSlot];
        const currentSurfaceRef = currentAssignment?.surfaceRef;
        if (!currentSurfaceRef) return;
        const surfaceRef: CatalogSurfaceReference = {
          ...currentSurfaceRef,
          color: nextColor,
        };
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          materialAssignments: {
            ...materialAssignments,
            [activeSlot]: {
              ...currentAssignment,
              surfaceRef,
            },
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      invalidate();
    };

    const commitEditableMaterialColor = () => {
      if (!materialColorUndoStartedRef.current) return;
      floorplannerStore.pushToUndoStack();
      materialColorUndoStartedRef.current = false;
    };

    const mergeEditableSurfaceRef = (
      current: CatalogSurfaceReference,
      overrides: Partial<CatalogSurfaceReference>,
    ): CatalogSurfaceReference => {
      const next: CatalogSurfaceReference = { ...current, ...overrides };
      (Object.keys(overrides) as Array<keyof CatalogSurfaceReference>).forEach(
        (key) => {
          if (overrides[key] === undefined) {
            delete next[key];
          }
        },
      );
      return next;
    };

    /**
     * Updates albedo UV repeat/rotation on the active built-in assignment.
     * Rotation is stored in radians (same as catalog wall surfaces).
     */
    const applyEditableMaterialTextureMapping = (
      overrides: Partial<
        Pick<
          CatalogSurfaceReference,
          "repeatScale" | "repeatScaleY" | "rotation"
        >
      >,
    ) => {
      if (
        overrides.repeatScale !== undefined &&
        (!Number.isFinite(overrides.repeatScale) || overrides.repeatScale <= 0)
      ) {
        return;
      }
      if (
        overrides.repeatScaleY !== undefined &&
        (!Number.isFinite(overrides.repeatScaleY) || overrides.repeatScaleY <= 0)
      ) {
        return;
      }
      if (
        overrides.rotation !== undefined &&
        !Number.isFinite(overrides.rotation)
      ) {
        return;
      }

      if (showShapeMaterialControls) {
        floorplannerStore.beginUndoTransaction("Edit material texture mapping");
        selectedShapeSymbols.forEach((symbol) => {
          const currentSurfaceRef =
            symbol.threeD?.surfaceRef ??
            symbol.threeD?.materialAssignments?.wall?.surfaceRef;
          if (!currentSurfaceRef) return;
          const surfaceRef = mergeEditableSurfaceRef(
            currentSurfaceRef,
            overrides,
          );
          floorplannerStore.recordSymbolForUndo(symbol.id);
          floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
            ...symbol.threeD,
            surfaceRef,
            materialAssignments: {
              ...(symbol.threeD?.materialAssignments ?? {}),
              wall: {
                ...(symbol.threeD?.materialAssignments?.wall ?? {
                  materialId: surfaceRef.id,
                }),
                surfaceRef,
              },
            },
          });
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
        });
        floorplannerStore.pushToUndoStack();
        invalidate();
        return;
      }

      if (showWallFloorMaterialControls) {
        floorplannerStore.beginUndoTransaction("Edit material texture mapping");
        selectedWalls.forEach((selected) => {
          const wall =
            floorplannerStore.wallsMap.get(selected.id) ?? selected;
          const currentSurfaceRef = wallMaterialTargetSide
            ? resolveWallFaceSurfaceRef(wall, wallMaterialTargetSide)
            : wall.threeD?.surfaceRef;
          if (!currentSurfaceRef) return;
          const surfaceRef = mergeEditableSurfaceRef(
            currentSurfaceRef,
            overrides,
          );
          floorplannerStore.recordWallForUndo(wall.id);
          floorplannerStore.setObjectProperty(
            wall.id,
            "threeD",
            setWallSurfaceThreeD(wall, surfaceRef),
          );
          floorplannerStore.recordWallChange(wall.id, "modify");
        });
        selectedAreaFillSymbols.forEach((selected) => {
          const symbol =
            floorplannerStore.symbolsMap.get(selected.id) ?? selected;
          const currentSurfaceRef = symbol.threeD?.surfaceRef;
          if (!currentSurfaceRef) return;
          const surfaceRef = mergeEditableSurfaceRef(
            currentSurfaceRef,
            overrides,
          );
          floorplannerStore.recordSymbolForUndo(symbol.id);
          floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
            ...symbol.threeD,
            surfaceRef,
          });
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
        });
        floorplannerStore.pushToUndoStack();
        invalidate();
        return;
      }

      if (!activeSlot) return;
      floorplannerStore.beginUndoTransaction("Edit material texture mapping");
      const materialTargets = resolveSlotMaterialTargets();
      materialTargets.forEach((symbol) => {
        const materialAssignments = symbol.threeD?.materialAssignments ?? {};
        const currentAssignment = materialAssignments[activeSlot];
        const currentSurfaceRef = currentAssignment?.surfaceRef;
        if (!currentSurfaceRef) return;
        const surfaceRef = mergeEditableSurfaceRef(
          currentSurfaceRef,
          overrides,
        );
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          materialAssignments: {
            ...materialAssignments,
            [activeSlot]: {
              ...currentAssignment,
              surfaceRef,
            },
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    /** Clears vertical tile size so wall textures return to aspect-based height. */
    const clearEditableMaterialRepeatScaleY = () => {
      applyEditableMaterialTextureMapping({ repeatScaleY: undefined });
    };

    const resetFillAndMaterials = () => {
      if (showWallFloorMaterialControls) {
        floorplannerStore.beginUndoTransaction("Reset fill and materials");
        selectedWalls.forEach((selected) => {
          const wall =
            floorplannerStore.wallsMap.get(selected.id) ?? selected;
          floorplannerStore.recordWallForUndo(wall.id);
          // Classic plaster — not pure white — matches getWallColor / 3D default.
          floorplannerStore.setObjectProperty(
            wall.id,
            "fillColor",
            DEFAULT_WALL_FILL_COLOR,
          );
          renderStore.updateJointFillStyleForWall(
            wall.id,
            DEFAULT_WALL_FILL_COLOR,
            undefined,
            1,
          );
          floorplannerStore.setObjectProperty(wall.id, "fillPattern", undefined);
          floorplannerStore.setObjectProperty(wall.id, "fillOpacity", 1);
          floorplannerStore.setObjectProperty(
            wall.id,
            "threeD",
            omitWallSurfaceMaterial(clearWallFaceMaterials(wall)),
          );
          floorplannerStore.recordWallChange(wall.id, "modify");
        });
        selectedAreaFillSymbols.forEach((selected) => {
          const symbol =
            floorplannerStore.symbolsMap.get(selected.id) ?? selected;
          floorplannerStore.recordSymbolForUndo(symbol.id);
          floorplannerStore.updateSymbolProperty(symbol.id, "fillColor", 0xffffff);
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "fillPattern",
            undefined,
          );
          floorplannerStore.updateSymbolProperty(symbol.id, "fillOpacity", 1);
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "threeD",
            omitSymbolDirectSurfaceMaterial(symbol.threeD, {
              clearAssignments: true,
              clearFillColorOverride: true,
            }),
          );
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
        });
        floorplannerStore.pushToUndoStack();
        invalidate();
        return;
      }

      const materialTargets = showShapeMaterialControls
        ? selectedShapeSymbols
        : showDoorMaterialControls
          ? selectedDoorSymbols
          : showStairsMaterialControls
            ? selectedStairSymbols
            : showSmartMaterialControls
              ? smartMaterialTargets
              : librarySvgForMaterials
                ? [librarySvgForMaterials]
                : [];
      if (materialTargets.length === 0) return;

      floorplannerStore.beginUndoTransaction("Reset fill and materials");
      materialTargets.forEach((symbol) => {
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "fillColor", 0xffffff);
        floorplannerStore.updateSymbolProperty(symbol.id, "fillPattern", undefined);
        floorplannerStore.updateSymbolProperty(symbol.id, "fillOpacity", 1);
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "threeD",
          omitSymbolDirectSurfaceMaterial(symbol.threeD, {
            clearAssignments: true,
            clearFillColorOverride: true,
          }),
        );
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const clearSurface = () => {
      floorplannerStore.beginUndoTransaction("Clear catalog surface");
      // Same as Ready "Remove material": drop materialId + surfaceRef on walls
      // so catalog Clear cannot leave a lingering Ready materialId.
      selectedWalls.forEach((selected) => {
        const wall =
          floorplannerStore.wallsMap.get(selected.id) ?? selected;
        floorplannerStore.recordWallForUndo(wall.id);
        floorplannerStore.setObjectProperty(
          wall.id,
          "threeD",
          setWallSurfaceThreeD(wall, null, { materialId: null }),
        );
        floorplannerStore.recordWallChange(wall.id, "modify");
      });
      selectedAreaFillSymbols.forEach((selected) => {
        const symbol =
          floorplannerStore.symbolsMap.get(selected.id) ?? selected;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "threeD",
          omitSymbolDirectSurfaceMaterial(symbol.threeD),
        );
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const clearProduct = () => {
      floorplannerStore.beginUndoTransaction("Clear catalog product");
      selectedSvgs.forEach((symbol) => {
        const nextSourceMode = symbol.threeD?.userModel ? "custom" : undefined;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: nextSourceMode,
          product: undefined,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const clearUserModel = () => {
      floorplannerStore.beginUndoTransaction("Clear uploaded 3D object");
      selectedSvgs.forEach((symbol) => {
        const nextSourceMode = symbol.threeD?.product ? "custom" : undefined;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: nextSourceMode,
          userModel: undefined,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const useDefault3DSource = () => {
      floorplannerStore.beginUndoTransaction("Use default 3D source");
      selectedSvgs.forEach((symbol) => {
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: undefined,
          product: undefined,
          userModel: undefined,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const usePrimitiveOnly3DSource = () => {
      floorplannerStore.beginUndoTransaction("Use primitive 3D source");
      selectedSvgs.forEach((symbol) => {
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: "primitiveOnly",
          product: undefined,
          userModel: undefined,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const openUserModelPicker = () => {
      const rect = userModelButtonRef.current?.getBoundingClientRect();
      setUserModelPanelPosition(
        rect
          ? {
              top: Math.max(16, Math.min(rect.top, window.innerHeight - 160)),
              left: Math.max(16, rect.right - 398),
            }
          : undefined,
      );
      setDrawerIntent("userModel");
    };

    const setProductRenderMode = (
      renderMode: "asset3D" | "primitiveProxy" | "billboardPreview",
    ) => {
      if (!firstProductRef) return;
      floorplannerStore.beginUndoTransaction("Change product preview mode");
      selectedSvgs.forEach((symbol) => {
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: "custom",
          product: symbol.threeD?.product
            ? {
                ...symbol.threeD.product,
                renderMode,
              }
            : undefined,
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      trackFloorplan3DMetric("product_render_mode", { renderMode });
      invalidate();
    };

    const mergeSurfaceRefOverrides = (
      current: NonNullable<typeof firstSurfaceRef>,
      overrides: Partial<NonNullable<typeof firstSurfaceRef>>,
    ): NonNullable<typeof firstSurfaceRef> => {
      const next = { ...current, ...overrides };
      // Allow clearing optional fields (e.g. repeatScaleY → auto aspect height).
      (Object.keys(overrides) as Array<keyof typeof overrides>).forEach(
        (key) => {
          if (overrides[key] === undefined) {
            delete next[key];
          }
        },
      );
      return next;
    };

    const updateSurfaceOverrides = (
      overrides: Partial<NonNullable<typeof firstSurfaceRef>>,
    ) => {
      if (!firstSurfaceRef) return;
      floorplannerStore.beginUndoTransaction(
        "Update catalog material settings",
      );
      selectedWalls.forEach((selected) => {
        const wall =
          floorplannerStore.wallsMap.get(selected.id) ?? selected;
        const currentSurfaceRef = wallMaterialTargetSide
          ? resolveWallFaceSurfaceRef(wall, wallMaterialTargetSide)
          : wall.threeD?.surfaceRef;
        if (!currentSurfaceRef) return;
        floorplannerStore.recordWallForUndo(wall.id);
        floorplannerStore.setObjectProperty(
          wall.id,
          "threeD",
          setWallSurfaceThreeD(
            wall,
            mergeSurfaceRefOverrides(currentSurfaceRef, overrides),
          ),
        );
        floorplannerStore.recordWallChange(wall.id, "modify");
      });
      selectedAreaFillSymbols.forEach((selected) => {
        const symbol =
          floorplannerStore.symbolsMap.get(selected.id) ?? selected;
        if (!symbol.threeD?.surfaceRef) return;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          surfaceRef: mergeSurfaceRefOverrides(
            symbol.threeD.surfaceRef,
            overrides,
          ),
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const updateProductDimension = (
      dimension: "width" | "height" | "depth",
      displayValue: number,
    ) => {
      if (!firstProductRef || !Number.isFinite(displayValue) || displayValue <= 0)
        return;
      const value = displayLengthToWorld(
        displayValue,
        editorStore.displayLengthUnit,
      );
      floorplannerStore.beginUndoTransaction("Update product dimensions");
      selectedSvgs.forEach((symbol) => {
        if (!symbol.threeD?.product) return;
        const currentDimensions =
          symbol.threeD.product.dimensions ??
          symbol.threeD.product.asset?.dimensions;
        const nextDimensions = isSymbolAspectRatioLocked(symbol.lockAspectRatio)
          ? scaleProduct3DDimensionsProportionally(
              currentDimensions,
              dimension,
              value,
            )
          : currentDimensions
            ? { ...currentDimensions, [dimension]: value }
            : undefined;
        if (
          !nextDimensions?.width ||
          !nextDimensions.depth ||
          !nextDimensions.height
        )
          return;
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "svgLength",
          nextDimensions.width,
        );
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "svgHeight",
          nextDimensions.depth,
        );
        floorplannerStore.updateSymbolProperty(
          symbol.id,
          "ratio",
          nextDimensions.width / nextDimensions.depth,
        );
        floorplannerStore.updateSymbolProperty(symbol.id, "threeD", {
          ...symbol.threeD,
          sourceMode: "custom",
          dimensions: nextDimensions,
          height: nextDimensions.height,
          product: {
            ...symbol.threeD.product,
            dimensions: nextDimensions,
            dimensionConfidence: "exact",
            dimensionUnit: "m",
            dimensionSource: "user",
          },
        });
        floorplannerStore.recordSymbolChange(symbol.id, "modify");
      });
      floorplannerStore.pushToUndoStack();
      invalidate();
    };

    const showDirectSurfaceSelectedCard =
      showDirectSurfaceMaterialControls &&
      (Boolean(selectedBuiltInMaterial) ||
        (showWallFloorMaterialControls && Boolean(firstSurfaceRef)) ||
        (showShapeMaterialControls &&
          Boolean(
            editableMaterialSurfaceRef?.id || selectedBuiltInMaterialId,
          )));
    const showFurnitureSelectedCard =
      showMaterialPicker &&
      !showDirectSurfaceMaterialControls &&
      Boolean(selectedBuiltInMaterial);
    const selectedSummaryImageUrl = selectedBuiltInMaterial
      ? (getBuiltInMaterialSurfaceMapping(selectedBuiltInMaterial.id)
          ?.imageUrl ?? selectedBuiltInMaterial.textureUrl)
      : showWallFloorMaterialControls && currentSurfaceImageUrl
        ? wallSurfaceSelected
          ? getWallFloorCatalogImageUrl(currentSurfaceImageUrl)
          : getResizedCatalogImageUrl(currentSurfaceImageUrl, 96)
        : undefined;
    const selectedSummaryTitle = selectedBuiltInMaterial
      ? selectedBuiltInMaterial.label ||
        (selectedBuiltInMaterial.labelKey
          ? t(selectedBuiltInMaterial.labelKey)
          : selectedBuiltInMaterial.id)
      : showWallFloorMaterialControls && firstSurfaceRef
        ? currentSurfaceLabel
        : t("propertyPanel.defaultMaterial");
    const selectedSummarySubtitle = selectedBuiltInMaterial
      ? (selectedBuiltInMaterial.categoryName ??
        selectedBuiltInMaterial.families[0])
      : currentSurfaceSubtitle;

    const clearSelectedMaterial = () => {
      if (showShapeMaterialControls) {
        clearShapeMaterial();
      } else if (showWallFloorMaterialControls) {
        clearWallFloorMaterial();
      } else {
        clearSlotMaterial();
      }
    };

    const canClearSelectedMaterial = showShapeMaterialControls
      ? Boolean(selectedShapeSymbols[0]?.threeD?.surfaceRef?.id)
      : showWallFloorMaterialControls
        ? Boolean(firstSurfaceRef || selectedBuiltInMaterialId)
        : Boolean(
            activeSlot &&
              (activeSlotAssignment?.materialId ||
                activeSlotAssignment?.surfaceRef?.id),
          );

    const selectedMaterialSummary = (
      <div className="rounded-sm border border-gray-200 bg-gray-50 p-2">
        <div className="mb-1 text-[11px] font-medium text-gray-500">
          {t("common.selected")}
        </div>
        <div className="flex items-center gap-2">
          {selectedSummaryImageUrl ? (
            <span
              className="h-8 w-8 shrink-0 rounded-sm bg-cover bg-center"
              style={{ backgroundImage: `url(${selectedSummaryImageUrl})` }}
            />
          ) : (
            <span
              className="h-8 w-8 shrink-0 rounded-sm border border-gray-300"
              style={{
                backgroundColor: `#${(
                  editableMaterialColor ??
                  selectedBuiltInMaterial?.color ??
                  0xffffff
                )
                  .toString(16)
                  .padStart(6, "0")}`,
              }}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium text-gray-900">
              {selectedSummaryTitle}
            </div>
            {selectedSummarySubtitle ? (
              <div className="truncate text-[10px] text-gray-500">
                {selectedSummarySubtitle}
              </div>
            ) : null}
          </div>
          {canClearSelectedMaterial && (
            <button
              type="button"
              className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              onClick={clearSelectedMaterial}
              aria-label={t("propertyPanel.clearMaterial")}
              title={t("propertyPanel.clearMaterialHint")}
            >
              <IoCloseOutline className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
        {canEditMaterialColor && editableMaterialColor !== undefined && (
          <div className="mt-2">
            <HsbColorSliders
              color={editableMaterialColor}
              onColorChange={applyEditableMaterialColor}
              onColorCommit={commitEditableMaterialColor}
            />
          </div>
        )}
        {canEditMaterialTextureMapping && (
          <div className="mt-2 space-y-2">
            {wallSurfaceSelected ? (
              <>
                <LinkedDimensionsGroup
                  locked={wallPatternAspectLocked}
                  onToggleLock={() =>
                    setWallPatternAspectLocked((locked) => !locked)
                  }
                  widthField={
                    <NumberControl
                      label={t("propertyPanel.repeatEveryWidthMeters")}
                      value={editableMaterialPatternSize}
                      min={0.1}
                      max={20}
                      step={0.1}
                      onChange={(patternSize) => {
                        if (!Number.isFinite(patternSize) || patternSize <= 0)
                          return;
                        applyEditableMaterialTextureMapping(
                          wallTileMappingFromLinkedEdit(
                            "width",
                            patternSize,
                            editableMaterialPatternSize,
                            editableWallTileHeightMeters,
                          ),
                        );
                      }}
                    />
                  }
                  lengthField={
                    <NumberControl
                      label={t("propertyPanel.repeatEveryHeightMeters")}
                      value={editableWallTileHeightMeters}
                      placeholder={t("propertyPanel.repeatHeightAuto")}
                      min={0.1}
                      max={20}
                      step={0.1}
                      allowEmpty
                      onChange={(meters) => {
                        if (!Number.isFinite(meters) || meters <= 0) {
                          clearEditableMaterialRepeatScaleY();
                          return;
                        }
                        applyEditableMaterialTextureMapping(
                          wallTileMappingFromLinkedEdit(
                            "height",
                            meters,
                            editableMaterialPatternSize,
                            editableWallTileHeightMeters,
                          ),
                        );
                      }}
                    />
                  }
                />
                <div className="grid grid-cols-2 gap-2">
                  <NumberControl
                    label={t("propertyPanel.rotate")}
                    value={editableMaterialRotationDegrees}
                    step={15}
                    onChange={(degrees) =>
                      applyEditableMaterialTextureMapping({
                        rotation: (degrees * Math.PI) / 180,
                      })
                    }
                  />
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <NumberControl
                  label={t("propertyPanel.patternSize")}
                  value={editableMaterialPatternSize}
                  min={0.1}
                  max={4}
                  step={0.1}
                  onChange={(patternSize) => {
                    if (!Number.isFinite(patternSize) || patternSize <= 0)
                      return;
                    applyEditableMaterialTextureMapping({
                      repeatScale: 1 / patternSize,
                    });
                  }}
                />
                <NumberControl
                  label={t("propertyPanel.rotate")}
                  value={editableMaterialRotationDegrees}
                  step={15}
                  onChange={(degrees) =>
                    applyEditableMaterialTextureMapping({
                      rotation: (degrees * Math.PI) / 180,
                    })
                  }
                />
              </div>
            )}
            {wallSurfaceSelected && firstSelectedWallHeight !== undefined && (
              <button
                type="button"
                className="flex min-h-7 w-full items-center justify-center rounded-sm border border-gray-300 bg-white px-2 py-1 text-center text-[11px] leading-tight hover:bg-gray-100"
                onClick={() =>
                  applyEditableMaterialTextureMapping(
                    wallTileMappingFromLinkedEdit(
                      "height",
                      firstSelectedWallHeight,
                      editableMaterialPatternSize,
                      editableWallTileHeightMeters,
                    ),
                  )
                }
              >
                {t("propertyPanel.fitWallHeight", {
                  height: firstSelectedWallHeight.toFixed(2),
                })}
              </button>
            )}
          </div>
        )}
      </div>
    );

    return (
      <>
        {showSurfaceControls && (
          <PanelSection
            title={t("propertyPanel.catalogMaterial")}
            collapsible
            open={catalogMaterialOpen}
            onToggle={() => setCatalogMaterialOpen((open) => !open)}
            maxHeightClassName="max-h-none"
          >
            <div className="space-y-3 p-3 text-sm">
              <p className="text-xs text-gray-500">
                {selectedWalls.length > 0
                  ? t("propertyPanel.wallMaterialHint")
                  : t("propertyPanel.floorMaterialHint")}
              </p>
              {soleWallForSideMaterials && (
                <div className="space-y-1">
                  <div className="text-[11px] font-medium text-gray-500">
                    {t("propertyPanel.wallMaterialSideTarget")}
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {(
                      [
                        ["left", "propertyPanel.wallSideOne"],
                        ["right", "propertyPanel.wallSideTwo"],
                      ] as const
                    ).map(([side, labelKey]) => {
                      const active = wallMaterialSide === side;
                      return (
                        <button
                          key={side}
                          type="button"
                          className={`rounded-sm border px-2 py-1.5 text-xs font-medium ${
                            active
                              ? "border-boniYellowPrimary bg-boniYellowPrimary text-black"
                              : "border-gray-300 bg-white text-black hover:bg-gray-50"
                          }`}
                          onClick={() => setWallMaterialSide(side)}
                        >
                          {t(labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Selected summary — same card language as Standard material. */}
              <div className="rounded-sm border border-gray-200 bg-gray-50 p-2">
                <div className="mb-1 text-[11px] font-medium text-gray-500">
                  {t("common.selected")}
                </div>
                <div className="flex items-center gap-2">
                  {currentSurfaceImageUrl ? (
                    <span
                      className="h-8 w-8 shrink-0 rounded-sm bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${
                          wallSurfaceSelected
                            ? getWallFloorCatalogImageUrl(currentSurfaceImageUrl)
                            : getResizedCatalogImageUrl(currentSurfaceImageUrl, 96)
                        })`,
                      }}
                    />
                  ) : (
                    <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-sm border border-gray-300 bg-white">
                      <span className="absolute left-1/2 top-1/2 h-[141%] w-0.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-red-500" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-gray-900">
                      {firstSurfaceRef
                        ? currentSurfaceLabel
                        : t("propertyPanel.noMaterialYet")}
                    </div>
                    {firstSurfaceRef && currentSurfaceSubtitle && (
                      <div className="truncate text-[10px] text-gray-500">
                        {currentSurfaceSubtitle}
                      </div>
                    )}
                  </div>
                  {firstSurfaceRef && (
                    <button
                      type="button"
                      className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                      onClick={clearSurface}
                      aria-label={t("propertyPanel.clearMaterial")}
                      title={t("propertyPanel.clearMaterialHint")}
                    >
                      <IoCloseOutline className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </div>

                {firstSurfaceRef && (
                  <div className="mt-2 space-y-2">
                    {wallSurfaceSelected ? (
                      <LinkedDimensionsGroup
                        locked={wallPatternAspectLocked}
                        onToggleLock={() =>
                          setWallPatternAspectLocked((locked) => !locked)
                        }
                        widthField={
                          <NumberControl
                            label={t("propertyPanel.repeatEveryWidthMeters")}
                            value={wallCatalogTileMeters}
                            min={0.1}
                            max={20}
                            step={0.1}
                            onChange={(meters) => {
                              if (!Number.isFinite(meters) || meters <= 0)
                                return;
                              updateSurfaceOverrides(
                                wallTileMappingFromLinkedEdit(
                                  "width",
                                  meters,
                                  wallCatalogTileMeters,
                                  wallCatalogTileHeightMeters,
                                ),
                              );
                            }}
                          />
                        }
                        lengthField={
                          <NumberControl
                            label={t("propertyPanel.repeatEveryHeightMeters")}
                            value={wallCatalogTileHeightMeters}
                            placeholder={t("propertyPanel.repeatHeightAuto")}
                            min={0.1}
                            max={20}
                            step={0.1}
                            allowEmpty
                            onChange={(meters) => {
                              if (!Number.isFinite(meters) || meters <= 0) {
                                updateSurfaceOverrides({
                                  repeatScaleY: undefined,
                                });
                                return;
                              }
                              updateSurfaceOverrides(
                                wallTileMappingFromLinkedEdit(
                                  "height",
                                  meters,
                                  wallCatalogTileMeters,
                                  wallCatalogTileHeightMeters,
                                ),
                              );
                            }}
                          />
                        }
                      />
                    ) : null}
                    <div className="grid grid-cols-2 gap-2">
                      {!wallSurfaceSelected && (
                        <NumberControl
                          label={t("propertyPanel.patternSize")}
                          value={Number(
                            (
                              1 /
                              Math.max(firstSurfaceRef.repeatScale ?? 1, 0.01)
                            ).toFixed(2),
                          )}
                          min={0.1}
                          max={4}
                          step={0.1}
                          onChange={(patternSize) => {
                            if (
                              !Number.isFinite(patternSize) ||
                              patternSize <= 0
                            )
                              return;
                            updateSurfaceOverrides({
                              repeatScale: 1 / patternSize,
                            });
                          }}
                        />
                      )}
                      <NumberControl
                        label={t("propertyPanel.rotate")}
                        value={Math.round(
                          ((firstSurfaceRef.rotation ?? 0) * 180) / Math.PI,
                        )}
                        step={15}
                        onChange={(degrees) =>
                          updateSurfaceOverrides({
                            rotation: (degrees * Math.PI) / 180,
                          })
                        }
                      />
                      <NumberControl
                        label={t("propertyPanel.rough")}
                        value={firstSurfaceRef.roughness ?? 0.78}
                        min={0}
                        max={1}
                        step={0.05}
                        onChange={(value) =>
                          updateSurfaceOverrides({ roughness: value })
                        }
                      />
                      <NumberControl
                        label={t("propertyPanel.metal")}
                        value={firstSurfaceRef.metalness ?? 0}
                        min={0}
                        max={1}
                        step={0.05}
                        onChange={(value) =>
                          updateSurfaceOverrides({ metalness: value })
                        }
                      />
                      <label className="block min-w-0 text-[11px] font-medium text-gray-500">
                        <span>{t("propertyPanel.quality")}</span>
                        <select
                          className="mt-1 h-7 w-full min-w-0 rounded-sm border border-gray-300 bg-white px-1 text-xs text-gray-900"
                          value={firstSurfaceRef.quality ?? "auto"}
                          onChange={(event) =>
                            updateSurfaceOverrides({
                              quality: event.target.value as
                                | "auto"
                                | "low"
                                | "standard"
                                | "high",
                            })
                          }
                        >
                          <option value="auto">{t("common.auto")}</option>
                          <option value="low">{t("propertyPanel.low")}</option>
                          <option value="standard">
                            {t("propertyPanel.standard")}
                          </option>
                          <option value="high">{t("propertyPanel.high")}</option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className="h-7 self-end rounded-sm border border-gray-300 bg-white px-1 text-[11px] hover:bg-gray-100"
                        onClick={() =>
                          floorplannerStore.updateThreeDMaterialDefault(
                            surfaceDefaultSlot,
                            {
                              repeatScale: firstSurfaceRef.repeatScale,
                              repeatScaleY: firstSurfaceRef.repeatScaleY,
                              rotation: firstSurfaceRef.rotation,
                              roughness: firstSurfaceRef.roughness,
                              metalness: firstSurfaceRef.metalness,
                              quality: firstSurfaceRef.quality,
                            },
                          )
                        }
                      >
                        {t("propertyPanel.setDefault")}
                      </button>
                    </div>
                    {wallSurfaceSelected &&
                      firstSelectedWallHeight !== undefined && (
                        <button
                          type="button"
                          className="flex min-h-7 w-full items-center justify-center rounded-sm border border-gray-300 bg-white px-2 py-1 text-center text-[11px] leading-tight hover:bg-gray-100"
                          onClick={() =>
                            updateSurfaceOverrides(
                              wallTileMappingFromLinkedEdit(
                                "height",
                                firstSelectedWallHeight,
                                wallCatalogTileMeters,
                                wallCatalogTileHeightMeters,
                              ),
                            )
                          }
                        >
                          {t("propertyPanel.fitWallHeight", {
                            height: firstSelectedWallHeight.toFixed(2),
                          })}
                        </button>
                      )}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="flex h-8 w-full items-center justify-center rounded-sm border border-gray-800 bg-white text-[11px] font-medium text-gray-900 hover:bg-gray-50"
                onClick={openProductMaterialBrowser}
              >
                {productMaterialBrowseLabel}
              </button>

              {firstSurfaceRef && (
                <div className="flex flex-col gap-1.5 border-t border-gray-200 pt-2">
                  <button
                    type="button"
                    className="h-7 w-full rounded-sm border border-gray-300 bg-gray-50 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                    title={t("propertyPanel.clearMaterialHint")}
                    onClick={clearSurface}
                  >
                    {t("propertyPanel.clearMaterial")}
                  </button>
                </div>
              )}
            </div>
          </PanelSection>
        )}
        {show3DSourceControls && (
          <PanelSection
            title={t("propertyPanel.source3D")}
            collapsible
            open={catalogProductOpen}
            onToggle={() => setCatalogProductOpen((open) => !open)}
          >
            <div className="space-y-2 p-3 text-sm">
              <div className="space-y-1.5">
                <SourceButton
                  active={
                    firstSymbolSourceMode !== "primitiveOnly" &&
                    !firstProductRef &&
                    !firstUserModelRef
                  }
                  onClick={useDefault3DSource}
                >
                  {t("common.default")}
                </SourceButton>
                <SourceButton
                  active={firstSymbolSourceMode === "primitiveOnly"}
                  onClick={usePrimitiveOnly3DSource}
                >
                  {t("propertyPanel.primitiveOnly")}
                </SourceButton>
                {catalogMaterialsEnabled && (
                  <SourceButton
                    active={Boolean(firstProductRef) && !firstUserModelRef}
                    onClick={() => setDrawerIntent("product")}
                  >
                    {t("propertyPanel.catalogProduct")}
                  </SourceButton>
                )}
                <SourceButton
                  ref={userModelButtonRef}
                  active={Boolean(firstUserModelRef)}
                  onClick={openUserModelPicker}
                >
                  {t("propertyPanel.uploadedObject")}
                </SourceButton>
              </div>
              {(firstProductRef?.productId || firstUserModelRef?.modelId) && (
                <div className="rounded-sm border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs text-gray-600">
                  {firstUserModelRef?.modelId && (
                    <div className="truncate">
                      {t("propertyPanel.objectLabel")}:{" "}
                      {firstUserModelRef.name ?? firstUserModelRef.modelId}
                    </div>
                  )}
                  {firstProductRef?.productId && (
                    <div className="truncate">
                      {t("propertyPanel.productLabel")}: {currentProductLabel}
                    </div>
                  )}
                  {firstProductRef?.productId && (
                    <div className="mt-1 inline-flex rounded-sm bg-gray-200 px-1.5 py-0.5 text-[11px] text-gray-700">
                      {firstProductRef.renderMode === "asset3D"
                        ? t("propertyPanel.object3D")
                        : firstProductRef.renderMode === "billboardPreview"
                          ? t("propertyPanel.imagePreview")
                          : t("propertyPanel.primitiveProxyLabel")}
                    </div>
                  )}
                  {firstProductRef?.fallbackReason && (
                    <div className="mt-1 text-[11px] text-gray-500">
                      {firstProductRef.fallbackReason}
                    </div>
                  )}
                </div>
              )}
              {(firstProductRef || firstUserModelRef) && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="text-[11px] font-medium leading-5 text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!firstProductRef}
                    onClick={clearProduct}
                  >
                    {t("propertyPanel.clearProduct")}
                  </button>
                  <button
                    type="button"
                    className="text-[11px] font-medium leading-5 text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!firstUserModelRef}
                    onClick={clearUserModel}
                  >
                    {t("propertyPanel.clearObject")}
                  </button>
                </div>
              )}
              {firstProductRef?.productId && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    className={`h-8 rounded-sm border text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                      firstProductRef.renderMode === "asset3D"
                        ? "border-gray-700 bg-gray-700 text-white"
                        : "border-gray-300 bg-white hover:bg-gray-100"
                    }`}
                    disabled={!firstProductRef.asset?.glbUrl}
                    onClick={() => setProductRenderMode("asset3D")}
                  >
                    {t("propertyPanel.view3D")}
                  </button>
                  <button
                    type="button"
                    className={`h-8 rounded-sm border text-xs ${
                      firstProductRef.renderMode === "primitiveProxy"
                        ? "border-gray-700 bg-gray-700 text-white"
                        : "border-gray-300 bg-white hover:bg-gray-100"
                    }`}
                    onClick={() => setProductRenderMode("primitiveProxy")}
                  >
                    {t("propertyPanel.proxy")}
                  </button>
                  <button
                    type="button"
                    className={`h-8 rounded-sm border text-xs ${
                      firstProductRef.renderMode === "billboardPreview"
                        ? "border-gray-700 bg-gray-700 text-white"
                        : "border-gray-300 bg-white hover:bg-gray-100"
                    }`}
                    onClick={() => setProductRenderMode("billboardPreview")}
                  >
                    {t("common.preview")}
                  </button>
                </div>
              )}
              {firstProductRef?.productId && (
                <div className="grid grid-cols-3 gap-2 rounded-sm border border-gray-200 bg-gray-50 p-2">
                  <NumberControl
                    label={`${t("propertyPanel.width")} (${lengthUnitLabel(editorStore.displayLengthUnit)})`}
                    value={worldToDisplayLength(
                      firstProductRef.dimensions?.width ?? 0,
                      editorStore.displayLengthUnit,
                      1,
                    )}
                    min={1}
                    step={1}
                    onChange={(value) => updateProductDimension("width", value)}
                  />
                  <NumberControl
                    label={`${t("propertyPanel.height")} (${lengthUnitLabel(editorStore.displayLengthUnit)})`}
                    value={worldToDisplayLength(
                      firstProductRef.dimensions?.height ?? 0,
                      editorStore.displayLengthUnit,
                      1,
                    )}
                    min={1}
                    step={1}
                    onChange={(value) =>
                      updateProductDimension("height", value)
                    }
                  />
                  <NumberControl
                    label={`${t("propertyPanel.depth")} (${lengthUnitLabel(editorStore.displayLengthUnit)})`}
                    value={worldToDisplayLength(
                      firstProductRef.dimensions?.depth ?? 0,
                      editorStore.displayLengthUnit,
                      1,
                    )}
                    min={1}
                    step={1}
                    onChange={(value) => updateProductDimension("depth", value)}
                  />
                  <div className="col-span-3 text-[11px] text-gray-500">
                    {firstProductRef.dimensionConfidence ?? "unknown"} /{" "}
                    {lengthUnitLabel(editorStore.displayLengthUnit)}
                  </div>
                </div>
              )}
            </div>
          </PanelSection>
        )}
        {showBuiltInMaterialControls && (
          <PanelSection
            title={t("propertyPanel.materials")}
            collapsible
            open={materialsOpen}
            onToggle={() => setMaterialsOpen((open) => !open)}
            // Avoid the default 500px clip so HSB / actions stay reachable.
            maxHeightClassName="max-h-none"
          >
            <div className="space-y-3 p-3 text-sm">
              {showDirectSurfaceMaterialControls ? (
                <>
                  <p className="text-xs text-gray-500">
                    {showShapeMaterialControls
                      ? t("propertyPanel.shapeMaterialHint")
                      : selectedWalls.length > 0
                        ? t("propertyPanel.wallMaterialHint")
                        : t("propertyPanel.floorMaterialHint")}
                  </p>
                  {soleWallForSideMaterials && (
                    <div className="space-y-1">
                      <div className="text-[11px] font-medium text-gray-500">
                        {t("propertyPanel.wallMaterialSideTarget")}
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {(
                          [
                            ["left", "propertyPanel.wallSideOne"],
                            ["right", "propertyPanel.wallSideTwo"],
                          ] as const
                        ).map(([side, labelKey]) => {
                          const active = wallMaterialSide === side;
                          return (
                            <button
                              key={side}
                              type="button"
                              className={`rounded-sm border px-2 py-1.5 text-xs font-medium ${
                                active
                                  ? "border-boniYellowPrimary bg-boniYellowPrimary text-black"
                                  : "border-gray-300 bg-white text-black hover:bg-gray-50"
                              }`}
                              onClick={() => setWallMaterialSide(side)}
                            >
                              {t(labelKey)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      {t("propertyPanel.productMaterialSlots")}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {t("propertyPanel.materialPartHint")}
                    </p>
                  </div>
                  {/*
                    Part cards — larger option tiles, distinct from category chips.
                    auto-fit stacks to one column in the narrow property panel so
                    labels like "Upholstery" stay fully readable.
                  */}
                  <div
                    className="grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-2"
                    role="tablist"
                  >
                    {materialSlots.map((slot) => {
                      const isActive = activeSlot === slot;
                      const optionKey =
                        productMaterialOptions &&
                        getProductMaterialOptionKeyForSlot(
                          productMaterialOptions,
                          slot,
                        );
                      const optionLabelKey =
                        optionKey && productMaterialOptions?.[optionKey]
                          ? productMaterialOptions[optionKey]!.labelKey
                          : undefined;
                      const slotLabel = optionLabelKey
                        ? t(optionLabelKey)
                        : optionKey
                          ? t(`propertyPanel.productOption.${optionKey}`)
                          : formatSlotLabel(slot, t);
                      const hasAssignment = Boolean(
                        materialAssignmentTarget?.threeD?.materialAssignments?.[
                          slot
                        ]?.materialId ||
                          materialAssignmentTarget?.threeD
                            ?.materialAssignments?.[slot]?.surfaceRef?.id,
                      );
                      return (
                        <button
                          key={slot}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          title={slotLabel}
                          className={`flex min-h-[44px] items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
                            isActive
                              ? "border-gray-900 bg-white shadow-sm ring-1 ring-gray-900"
                              : "border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300 hover:bg-white"
                          }`}
                          onClick={() => {
                            setActiveSlot(slot);
                            setDrawerIntent(null);
                          }}
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                              isActive
                                ? "border-gray-900 bg-gray-900 text-white"
                                : "border-gray-400 bg-white"
                            }`}
                            aria-hidden
                          >
                            {isActive ? (
                              <span className="h-1.5 w-1.5 rounded-full bg-white" />
                            ) : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-xs font-semibold leading-snug text-gray-900">
                              {slotLabel}
                            </span>
                            {hasAssignment ? (
                              <span className="block text-[10px] leading-snug text-gray-500">
                                {t("common.selected")}
                              </span>
                            ) : (
                              <span className="block text-[10px] leading-snug text-gray-400">
                                {t("propertyPanel.noMaterialYet")}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              {showMaterialPicker && (
                <div className="space-y-2">
                  {showDirectSurfaceSelectedCard && selectedMaterialSummary}
                  {showCatalogBrowseInStandardMaterials && (
                    <>
                      <button
                        type="button"
                        className="flex h-9 w-full items-center justify-center rounded-sm border border-gray-900 bg-gray-900 text-[12px] font-medium text-white hover:bg-gray-800"
                        onClick={openProductMaterialBrowser}
                      >
                        {productMaterialBrowseLabel}
                      </button>
                      {catalogSurfaceProductPreviewCards.length > 0 && (
                        <div className="grid grid-cols-3 gap-1.5">
                          {catalogSurfaceProductPreviewCards.map((product) => {
                            const thumbUrl = product.images?.[0]
                              ? getWallFloorCatalogImageUrl(product.images[0])
                              : undefined;
                            const label = getCatalogItemDisplayName(product);
                            const isSelected =
                              firstSurfaceRef?.source === "product" &&
                              firstSurfaceRef.id === product.id;
                            return (
                              <button
                                key={product.id}
                                type="button"
                                aria-pressed={isSelected}
                                title={label}
                                className={`flex min-w-0 flex-col items-stretch overflow-hidden rounded-sm border text-left ${
                                  isSelected
                                    ? "border-gray-800 ring-1 ring-gray-800"
                                    : "border-gray-300 hover:border-gray-400"
                                }`}
                                onClick={() =>
                                  beginCatalogSurfaceProductFromPreview(product)
                                }
                              >
                                {thumbUrl ? (
                                  <span
                                    className="h-8 w-full shrink-0 bg-cover bg-center"
                                    style={{
                                      backgroundImage: `url(${thumbUrl})`,
                                    }}
                                  />
                                ) : (
                                  <span className="h-8 w-full shrink-0 bg-gray-100" />
                                )}
                                <span className="mt-0.5 line-clamp-2 w-full min-w-0 break-words px-[3px] py-0.5 text-center text-[10px] font-normal leading-[12px] text-gray-700">
                                  {label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div
                        className="flex items-center gap-2 pt-1"
                        role="separator"
                        aria-label={t("propertyPanel.orChooseBoniMaterials")}
                      >
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="shrink-0 text-[11px] font-medium text-gray-600">
                          {t("propertyPanel.orChooseBoniMaterials")}
                        </span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                    </>
                  )}
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      className={`rounded-sm border px-2 py-0.5 text-[11px] ${
                        materialCategoryFilter === null
                          ? "border-gray-800 bg-gray-800 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => setMaterialCategoryFilter(null)}
                    >
                      {t("propertyPanel.materialCategoryAll")}
                    </button>
                    {builtInCategoryGroups.map((group) => {
                      const isActive =
                        materialCategoryFilter === group.categoryKey;
                      return (
                        <button
                          key={group.categoryKey}
                          type="button"
                          className={`rounded-sm border px-2 py-0.5 text-[11px] ${
                            isActive
                              ? "border-gray-800 bg-gray-800 text-white"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            setMaterialCategoryFilter(group.categoryKey)
                          }
                        >
                          {group.categoryLabel}
                        </button>
                      );
                    })}
                    {catalogMaterialCategoryKey &&
                      catalogMaterialCategoryLabel && (
                        <button
                          type="button"
                          className={`rounded-sm border px-2 py-0.5 text-[11px] ${
                            materialCategoryFilter === catalogMaterialCategoryKey
                              ? "border-gray-800 bg-gray-800 text-white"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            setMaterialCategoryFilter(catalogMaterialCategoryKey)
                          }
                        >
                          {catalogMaterialCategoryLabel}
                        </button>
                      )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {showingCatalogMaterialCategory
                      ? previewCatalogMaterials.map((product) => {
                          const thumbUrl = product.images?.[0]
                            ? getWallFloorCatalogImageUrl(product.images[0])
                            : undefined;
                          const label = getCatalogItemDisplayName(product);
                          const isSelected =
                            firstSurfaceRef?.source === "product" &&
                            firstSurfaceRef.id === product.id;
                          return (
                            <button
                              key={product.id}
                              type="button"
                              aria-pressed={isSelected}
                              title={label}
                              className={`flex min-w-0 flex-col items-stretch overflow-hidden rounded-sm border text-left ${
                                isSelected
                                  ? "border-gray-800 ring-1 ring-gray-800"
                                  : "border-gray-300 hover:border-gray-400"
                              }`}
                              onClick={() =>
                                beginCatalogSurfaceProductFromPreview(product)
                              }
                            >
                              {thumbUrl ? (
                                <span
                                  className="h-8 w-full shrink-0 bg-cover bg-center"
                                  style={{
                                    backgroundImage: `url(${thumbUrl})`,
                                  }}
                                />
                              ) : (
                                <span className="h-8 w-full shrink-0 bg-gray-100" />
                              )}
                              <span className="mt-0.5 line-clamp-2 w-full min-w-0 break-words px-[3px] py-0.5 text-center text-[10px] font-normal leading-[12px] text-gray-700">
                                {label}
                              </span>
                            </button>
                          );
                        })
                      : previewBuiltInMaterials.map((preset) => {
                          const isSelected =
                            selectedBuiltInMaterialId === preset.id;
                          const adminSurfaceImage =
                            getBuiltInMaterialSurfaceMapping(preset.id)
                              ?.imageUrl ?? preset.textureUrl;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              aria-pressed={isSelected}
                              title={
                                preset.label ||
                                (preset.labelKey
                                  ? t(preset.labelKey)
                                  : preset.id)
                              }
                              className={`flex min-w-0 flex-col items-stretch overflow-hidden rounded-sm border text-left ${
                                isSelected
                                  ? "border-gray-800 ring-1 ring-gray-800"
                                  : "border-gray-300 hover:border-gray-400"
                              }`}
                              onClick={() => applyBuiltInMaterial(preset)}
                            >
                              {adminSurfaceImage ? (
                                <span
                                  className="h-8 w-full shrink-0 bg-cover bg-center"
                                  style={{
                                    backgroundImage: `url(${adminSurfaceImage})`,
                                  }}
                                />
                              ) : (
                                <span
                                  className="h-8 w-full shrink-0"
                                  style={{
                                    backgroundColor: `#${(
                                      isSelected &&
                                      editableMaterialColor !== undefined
                                        ? editableMaterialColor
                                        : preset.color
                                    )
                                      .toString(16)
                                      .padStart(6, "0")}`,
                                  }}
                                />
                              )}
                              <span className="mt-0.5 line-clamp-2 w-full min-w-0 break-words px-[3px] py-0.5 text-center text-[10px] font-normal leading-[12px] text-gray-700">
                                {preset.label ||
                                  (preset.labelKey
                                    ? t(preset.labelKey)
                                    : preset.id)}
                              </span>
                            </button>
                          );
                        })}
                  </div>
                  <button
                    type="button"
                    className="flex h-8 w-full items-center justify-center rounded-sm border border-gray-800 bg-white text-[11px] font-medium text-gray-900 hover:bg-gray-50"
                    onClick={() => setDrawerIntent("browseMaterial")}
                  >
                    {hasMoreBuiltInMaterials
                      ? t("propertyPanel.showMoreMaterials")
                      : t("propertyPanel.browseMaterials")}
                  </button>
                  {showFurnitureSelectedCard && selectedMaterialSummary}
                </div>
              )}
              <div className="flex flex-col gap-1.5 border-t border-gray-200 pt-2">
                {canClearSelectedMaterial && (
                  <button
                    type="button"
                    className="h-7 w-full rounded-sm border border-gray-300 bg-gray-50 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                    title={t("propertyPanel.clearMaterialHint")}
                    onClick={clearSelectedMaterial}
                  >
                    {t("propertyPanel.clearMaterial")}
                  </button>
                )}
                <button
                  type="button"
                  className="self-start text-[10px] text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                  title={t("propertyPanel.resetFillAndMaterialHint")}
                  onClick={resetFillAndMaterials}
                >
                  {t("propertyPanel.resetFillAndMaterial")}
                </button>
              </div>
              {activeSlot &&
                activeSlotAssignment?.surfaceRef &&
                !getBuiltInFurnitureMaterial(
                  activeSlotAssignment.materialId ??
                    activeSlotAssignment.surfaceRef.id,
                ) && (
                  <div className="truncate text-[11px] text-gray-500">
                    {formatSlotLabel(activeSlot, t)}:{" "}
                    {getCatalogEntryDisplayName(
                      activeSlotEntry,
                      activeSlotAssignment.surfaceRef.id,
                    )}
                  </div>
                )}
            </div>
          </PanelSection>
        )}
        {catalogMaterialsEnabled && drawerIntent === "surface" && (
          <ProductsPanel
            intent={
              selectedWalls.length > 0 ? "wallMaterial" : "areaFillMaterial"
            }
            onClose={() => setDrawerIntent(null)}
            onSurfaceSelect={applySurface}
          />
        )}
        {catalogMaterialsEnabled &&
          drawerIntent === "wallpaper" &&
          selectedWalls.length > 0 && (
            <ProductsPanel
              intent="wallpaperProduct"
              initialTexturePickProduct={texturePickSeedProduct}
              onClose={closeCatalogDrawer}
              onProductSelect={applyWallpaperProduct}
            />
          )}
        {catalogMaterialsEnabled &&
          drawerIntent === "flooring" &&
          selectedAreaFillSymbols.length > 0 && (
            <ProductsPanel
              intent="flooringProduct"
              initialTexturePickProduct={texturePickSeedProduct}
              onClose={closeCatalogDrawer}
              onProductSelect={applyFlooringProduct}
            />
          )}
        {show3DSourceControls &&
          catalogMaterialsEnabled &&
          drawerIntent === "product" && (
            <ProductsPanel
              intent="product"
              initialSearch={productSearchText}
              proxyResolver={(product) =>
                resolveProductProxyForSymbol(product, selectedSymbolKey)
                  .renderMode
              }
              onClose={() => setDrawerIntent(null)}
              onProductSelect={applyProduct}
            />
          )}
        {showBuiltInMaterialControls &&
          drawerIntent === "browseMaterial" && (
            <BuiltInMaterialsPanel
              selectedMaterialId={selectedBuiltInMaterialId}
              initialCategoryKey={materialCategoryFilter}
              onCategoryChange={setMaterialCategoryFilter}
              catalogBrowse={
                catalogMaterialCategoryKey && catalogMaterialCategoryLabel
                  ? {
                      intent:
                        catalogMaterialCategoryKey ===
                        CATALOG_MATERIAL_CATEGORY_WALLPAPER
                          ? "wallpaperProduct"
                          : "flooringProduct",
                      categoryKey: catalogMaterialCategoryKey,
                      label: catalogMaterialCategoryLabel,
                      onProductSelect:
                        catalogMaterialCategoryKey ===
                        CATALOG_MATERIAL_CATEGORY_WALLPAPER
                          ? applyWallpaperProduct
                          : applyFlooringProduct,
                    }
                  : null
              }
              onClose={closeCatalogDrawer}
              onSelect={applyBuiltInMaterial}
            />
          )}
        {show3DSourceControls && drawerIntent === "userModel" && (
          <UserModels3DPanel
            mode="select"
            position={userModelPanelPosition}
            dropdownRef={userModelPanelRef}
            selectedModelId={firstUserModelRef?.modelId}
            onClose={() => setDrawerIntent(null)}
            onModelSelect={applyUserModel}
          />
        )}
        {showMaterial3DPreviewPrompt && (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                dismissMaterial3DPreviewPrompt();
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="material-3d-preview-title"
              className="w-full max-w-sm rounded-md border border-gray-200 bg-white p-4 shadow-lg"
            >
              <p
                id="material-3d-preview-title"
                className="text-sm leading-snug text-gray-900"
              >
                {t("propertyPanel.materialPreviewIn3DHint")}
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  className="flex h-9 w-full items-center justify-center rounded-sm border border-gray-900 bg-gray-900 text-[13px] font-medium text-white hover:bg-gray-800"
                  onClick={switchTo3DToPreviewMaterials}
                >
                  {t("toolbar.switchTo3d")}
                </button>
                <button
                  type="button"
                  className="flex h-9 w-full items-center justify-center rounded-sm border border-gray-300 bg-white text-[13px] font-medium text-gray-900 hover:bg-gray-50"
                  onClick={dismissMaterial3DPreviewPrompt}
                >
                  {t("propertyPanel.materialPreviewStayIn2d")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  });

const NumberControl: React.FC<{
  label: string;
  value: number | "";
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  /** When true, clearing the field commits empty (NaN) instead of min. */
  allowEmpty?: boolean;
  onChange: (value: number) => void;
}> = ({
  label,
  value,
  placeholder,
  min,
  max,
  step = 1,
  allowEmpty = false,
  onChange,
}) => (
  <NumberField
    label={label}
    value={value === "" ? "" : Number.isFinite(value) ? value : 0}
    placeholder={placeholder}
    min={min}
    max={max}
    step={step}
    emptyValue={allowEmpty ? "" : (min ?? 0)}
    fieldClassName="block min-w-0"
    labelClassName="mb-1 flex min-h-[18px] items-end break-words text-[11px] font-medium leading-tight text-gray-600"
    inputClassName="h-7 w-full min-w-0 overflow-hidden text-ellipsis rounded-sm border border-gray-300 bg-white px-1 text-xs text-gray-900 placeholder:overflow-hidden placeholder:text-ellipsis focus:border-blue-500 focus:ring-blue-500"
    onChange={(nextValue) =>
      onChange(nextValue === "" ? Number.NaN : Number(nextValue))
    }
  />
);

const SourceButton = React.forwardRef<
  HTMLButtonElement,
  {
    active?: boolean;
    children: React.ReactNode;
    onClick: () => void;
  }
>(({ active = false, children, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-pressed={active}
    className={`flex h-10 w-full min-w-0 items-center gap-2 rounded-sm border px-3 text-left text-sm font-medium leading-5 transition-colors ${
      active
        ? "border-gray-800 bg-gray-50 text-gray-950"
        : "border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50"
    }`}
    onClick={onClick}
  >
    <span
      className={`h-2.5 w-2.5 shrink-0 rounded-full border ${
        active ? "border-gray-900 bg-gray-900" : "border-gray-400 bg-white"
      }`}
    />
    <span className="min-w-0 flex-1 truncate">{children}</span>
  </button>
));
SourceButton.displayName = "SourceButton";

function parseColor(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const normalized = value.startsWith("#") ? value.slice(1) : value;
  const parsed = Number.parseInt(normalized, 16);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatSlotLabel(
  slot: FloorPlan3DMaterialSlot,
  t: (key: string) => string,
): string {
  const translated = t(`propertyPanel.materialSlot.${slot}`);
  if (translated !== `propertyPanel.materialSlot.${slot}`) {
    return translated;
  }

  return slot
    .replace(/^furniture/, "")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());
}

export default ProductAndMaterialsSection;
